/**
 * Der Arbeitsbereich TECHNIK -- kompakte Leseansicht und ein Bearbeitungsmodus.
 *
 * WAS SICH GEGENUEBER DER BISHERIGEN ANSICHT AENDERT
 *
 * Bis hierher trug dieser Reiter `ProductProperties`: eine lange, einspaltige
 * Liste, in der jedes Merkmal seinen eigenen Bleistift, seinen eigenen
 * Pruefstand und -- beim Aufklappen -- sein eigenes Formular hatte. Zweiund-
 * zwanzig Merkmale ergaben zweiundzwanzig Bedienelemente und drei Bildschirm-
 * hoehen. Wer LESEN wollte, las ein Pflegewerkzeug.
 *
 * Jetzt zwei Ansichten mit demselben Inhalt:
 *
 *   LESEN       zwei Spalten, fuenf Themengruppen, eine Zeile je Merkmal.
 *               Kein Bleistift, keine roten Flaechen. Konflikte sind
 *               erkennbar, BEVOR man etwas aufklappt; Quellwortlaute stehen
 *               je Gruppe in einem zugeklappten Bereich.
 *
 *   BEARBEITEN  dieselbe Anordnung, dieselben Zeilen -- nur sind die Werte
 *               jetzt Eingabefelder. Der Modus wird ueber EINEN Stift in der
 *               Bereichsleiste betreten und ist als Modus gekennzeichnet.
 *
 * DIE GRUPPIERUNG KOMMT AUS DEM KATALOG, NICHT AUS DIESER DATEI.
 *
 * Welche Gruppe ein Merkmal traegt, steht in `property_definitions.group_code`
 * (`PROPERTY_GROUPS`). Diese Datei kennt keinen einzigen Merkmalscode und
 * keinen Artikel. Ein neues Merkmal erscheint automatisch in seiner Gruppe.
 *
 * FUENF ZUSTAENDE, DIE NICHT ZU EINEM ROT VERSCHMELZEN DUERFEN
 *
 *   nicht bestaetigt   der Pruefstand ist nicht `belastbar`     (dezent rot)
 *   Quellenkonflikt    ein Befund oder ein offener Vorschlag    (bernstein)
 *   nicht gespeichert  im Entwurf geaendert, noch nicht gesandt (Akzent)
 *   Eingabefehler      die Eingabe ist so nicht speicherbar     (kraeftig rot)
 *   gerade gespeichert in DIESEM Gang gesandt, noch unbestaetigt (Akzentfuellung)
 *
 * Jeder Zustand traegt zusaetzlich zur Farbe eine Beschriftung. Farbe allein
 * waere fuer einen Teil der Leser gar keine Auskunft.
 *
 * Der fuenfte ist der einzige, der nicht aus den Daten kommt, sondern aus dem
 * Arbeitsgang -- und der einzige, der mit dem Bearbeitungsmodus endet.
 *
 * SPEICHERN IST KEINE FACHLICHE BESTAETIGUNG.
 *
 * Der Server nimmt eine Bestaetigung bei einer Wertaenderung ausdruecklich
 * zurueck (`updateProperty`), und `belastbar` verlangt `property.confirm`.
 * Diese Ansicht bildet das ab: Bestaetigen ist eine eigene, benannte Handlung
 * und niemals eine Nebenwirkung des Speicherns.
 *
 * WOHL ABER STEHT SIE DORT, WO SIE ANSTEHT.
 *
 * Bis hierher endete der Bearbeitungsmodus beim vollstaendigen Speichern --
 * und die Bestaetigung des eben gespeicherten Wertes verlangte einen zweiten
 * kompletten Durchlauf. Der Modus bleibt jetzt offen; der gespeicherte Wert
 * sagt, dass er gespeichert und noch nicht bestaetigt ist, und bietet die
 * Bestaetigung an Ort und Stelle an. Getrennt bleiben die beiden Handlungen
 * deshalb genauso -- nur nicht mehr durch einen Umweg.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ArticleFinding, BreakerCapacity, MetaResponse, PropertyDefinition, PropertyGroup,
  PropertyValue, QualityStatusCode, ValueProposalRef,
  ArticleRelationRef, ArticleCapability, VariantValueRef,
} from "@homebase/shared";
import type { ArticleRequirements, RequirementCheck } from "@homebase/shared";
import { PROPERTY_GROUPS } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { propertyLabel, type Merkmalskontext } from "../i18n/terminology.ts";
import { zeigeUndFokussiere } from "../lib/fokus.ts";
import { errorText } from "../i18n/errors.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import { displayValue } from "./ProductProperties.tsx";
import "./ArticleTechnik.css";

/* ── Der Entwurf ──────────────────────────────────────────────────────────
 *
 * Was im Bearbeitungsmodus in den Feldern steht, aber noch nicht gesandt
 * wurde. Der Schluessel ist die Wert-ID; gespeichert wird nur, was tatsaechlich
 * von seinem Ausgangswert abweicht.
 *
 * ZEICHENKETTEN, KEINE ZAHLEN. Was jemand tippt, ist Text -- auch "4,5" und
 * auch "". Erst beim Speichern wird daraus eine Zahl oder ein Fehler; eine
 * vorzeitige Umwandlung verschluckt genau die Eingaben, ueber die zu reden
 * ist.
 */
type Feld = Record<string, string | boolean>;

/** Der Ausgangszustand eines Wertes als Feldbelegung. */
function ausWert(v: PropertyValue): Feld {
  return {
    num: v.num ?? "", min: v.min ?? "", max: v.max ?? "",
    bool: v.bool ?? false, code: v.code ?? "", text: v.text ?? "",
  };
}

const gleich = (a: Feld, b: Feld): boolean =>
  ["num", "min", "max", "bool", "code", "text"].every((k) => String(a[k] ?? "") === String(b[k] ?? ""));

/**
 * Die Steuerung, die der Rahmen braucht.
 *
 * Der Reiterwechsel, der Artikelwechsel und das Schliessen passieren AUSSERHALB
 * dieser Komponente. Damit dort niemand ungespeicherte Eingaben verliert,
 * meldet diese Ansicht drei Dinge nach oben: ob etwas offen ist, wie man es
 * speichert und wie man es verwirft.
 */
export interface TechnikSteuerung {
  readonly dirty: boolean;
  /** Speichert alles Offene. `true`, wenn danach nichts mehr offen ist. */
  readonly save: () => Promise<boolean>;
  readonly discard: () => void;
}

/** Eine Merkmalszeile: Definition, ihre Werte, ihre Befunde, ihre Vorschlaege. */
interface Zeile {
  readonly def: PropertyDefinition;
  readonly values: readonly PropertyValue[];
  readonly befunde: readonly ArticleFinding[];
  readonly vorschlaege: readonly ValueProposalRef[];
  /**
   * Der Code des Produktmodells, wenn ALLE Werte dieser Zeile von dort
   * kommen -- sonst `null`. Gemischt kann eine Zeile nicht sein: sobald die
   * SKU einen eigenen Wert zu einer Definition traegt, erbt sie zu dieser
   * Definition nichts mehr.
   */
  readonly geerbt: string | null;
  /**
   * Die erwartete Information aus dem Datenanforderungsprofil -- null, wenn
   * dieses Merkmal fuer diese Art von Produkt nicht erwartet wird
   * (Produktdaten 001 A5).
   */
  readonly anforderung?: RequirementCheck | null;
  /** Die Definition ist eine Achse der Ausfuehrung dieser SKU. */
  readonly ausAusfuehrung: boolean;
}

interface Gruppe {
  readonly code: PropertyGroup;
  readonly zeilen: readonly Zeile[];
}

/**
 * DIE ZWEI SPALTEN STEHEN FEST -- SIE WERDEN NICHT MEHR NACH HOEHE VERTEILT.
 *
 * Bis hierher bekam die jeweils kuerzere Spalte die naechste Gruppe. Das
 * ergab ein gleichmaessiges Raster und eine Anordnung, die sich mit jedem
 * Datenstand aenderte: an einem Artikel ohne Optikwerte stand die Elektrik
 * links, am naechsten rechts. Wer zwei Ausfuehrungen desselben Produkts
 * nebeneinander liest, sucht dann jedes Mal neu.
 *
 * Deshalb eine feste Ordnung: links das, was die Leuchte LEISTET
 * (Lichttechnik, Schutz, Optik), rechts das, was sie BRAUCHT und WIEGT
 * (Elektrik, Mechanik) -- und die Nachweise am Ende, weil sie das Ergebnis
 * belegen und nicht beschreiben.
 *
 * WAS HIER NICHT STEHT, VERSCHWINDET TROTZDEM NICHT. Eine kuenftige sechste
 * Merkmalsgruppe kaeme aus dem Katalog und nicht aus dieser Liste; sie wird
 * unten an die kuerzere Spalte gehaengt. Ohne diesen Auffang waere eine neue
 * Gruppe in dieser Ansicht unsichtbar -- und niemand saehe, dass sie fehlt.
 */
const SPALTENORDNUNG: readonly (readonly PropertyGroup[])[] = [
  ["lichttechnik", "schutz", "optik"],
  ["elektrik", "mechanik", "konformitaet"],
];

/**
 * Der Pruef- und Befundstand des Artikels in vier Zahlen.
 *
 * `bestaetigt` ist `null`, wenn der Pruefstand gar nicht geliefert wurde --
 * das ist ausdruecklich etwas anderes als `0`.
 */
interface Stand {
  readonly werte: number;
  readonly bestaetigt: number | null;
  readonly befunde: number;
  readonly vorschlaege: number;
}

/**
 * Der Stand als eine Zeile: „23 technische Werte · 4 Befunde · 1 offener
 * Vorschlag".
 *
 * NUR WAS DA IST, WIRD GENANNT.
 *
 * Die Gesamtzahl steht immer, jedes weitere Segment nur mit einer Zahl
 * groesser als null. „0 Befunde" waere eine Entwarnung, um die niemand
 * gebeten hat, und „0 fachlich bestaetigt" waere der Satz „hier ist noch
 * nichts fertig" -- also genau die Vollstaendigkeitsaussage, die dieser
 * Bereich nicht treffen soll. Sobald jemand einen Wert bestaetigt, erscheint
 * die Zahl, und dann sagt sie etwas.
 *
 * EIN- UND MEHRZAHL ALS EIGENE SCHLUESSEL. Die Uebersetzung dieser Anwendung
 * kennt bewusst keine Pluralregeln (`i18n/index.tsx`). „1 offene Vorschläge"
 * waere in einer Zeile, die Ruhe herstellen soll, genau der Stolperstein,
 * der sie wieder nimmt.
 */
function standText(stand: Stand, i18n: I18n): string {
  const t = i18n.t;
  const teile = [
    stand.werte === 1 ? t("tk.standWert") : t("tk.standWerte", { n: stand.werte }),
  ];
  if (stand.bestaetigt !== null && stand.bestaetigt > 0) {
    teile.push(t("tk.standBestaetigt", { n: stand.bestaetigt }));
  }
  if (stand.befunde > 0) {
    teile.push(stand.befunde === 1
      ? t("tk.standBefund") : t("tk.standBefunde", { n: stand.befunde }));
  }
  if (stand.vorschlaege > 0) {
    teile.push(stand.vorschlaege === 1
      ? t("tk.standVorschlag") : t("tk.standVorschlaege", { n: stand.vorschlaege }));
  }
  return teile.join(" · ");
}

export function ArticleTechnik({
  articleId, meta, mayWrite, mayConfirm, mayWriteBreaker,
  editing, onEditingChange, steuerung, attention, onOpenArticle, ausfuehrung,
}: {
  articleId: number;
  /**
   * Die Achsen der Ausfuehrung, in der diese SKU steht -- oder null.
   *
   * DIE HERKUNFT EINES WERTES IST EINE ANGABE FUER SICH (UI/UX 001).
   *
   * Ein Merkmalswert einer SKU kann aus drei Richtungen kommen: vom
   * PRODUKTMODELL (geerbt, `inherited`), aus der AUSFUEHRUNG (beim Zuordnen
   * uebernommen -- technisch ein eigener Wert der SKU, fachlich die Achse
   * ihrer Ausfuehrung) oder von der SKU selbst. Die ersten beiden sahen bis
   * hierher gleich aus wie der dritte, und genau daran scheiterte das Lesen:
   * ein aus einer Ausfuehrung erzeugter Artikel wirkte leer gepflegt.
   *
   * Woher die Achsen kommen: aus der Akte, die der Aufrufer ohnehin hat --
   * kein zweiter Abruf, keine neue Vertragsfläche.
   */
  ausfuehrung: readonly VariantValueRef[] | null;
  /** Einen ANDEREN Artikel oeffnen -- etwa den verbauten Treiber. */
  onOpenArticle: (id: number) => void;
  meta: MetaResponse;
  /** `property.write`. Die Oberflaeche richtet sich danach; es entscheidet der Server. */
  mayWrite: boolean;
  /** `property.confirm` -- ein eigenes Recht, kein Nebenprodukt von `mayWrite`. */
  mayConfirm: boolean;
  /*
   * `article.write` -- wieder ein eigenes Recht.
   *
   * Die Leuchten je Leitungsschutzschalter haengen am ARTIKEL und nicht am
   * Merkmalskatalog; der Server verlangt dafuer `article.write`
   * (`server.ts`, /breaker-capacities). Wer Merkmale pflegen darf, darf
   * deshalb nicht automatisch auch diese Zeilen anlegen -- und die
   * Oberflaeche darf es nicht anbieten.
   */
  mayWriteBreaker: boolean;
  editing: boolean;
  onEditingChange: (v: boolean) => void;
  /** Wird mit der aktuellen Steuerung belegt -- siehe `TechnikSteuerung`. */
  steuerung: React.MutableRefObject<TechnikSteuerung | null>;
  /** Die Aufmerksamkeitszone des Bereichs. Sie gehoert dem Rahmen, nicht hierher. */
  attention?: React.ReactNode;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  /* Steht hier oben, weil die Kartenverteilung weiter unten davon abhaengt:
     der Schalterblock erscheint im Bearbeitungsmodus auch dann, wenn der
     Artikel noch gar keine Kapazitaet traegt. */
  const bearbeitet = editing && mayWrite;
  const [items, setItems] = useState<readonly PropertyValue[] | null>(null);
  /*
   * DIE GEERBTEN WERTE STEHEN IN `items` -- UND SIND HIER VERMERKT.
   *
   * Der Server liefert sie getrennt (`inherited`), damit wer MISST sie nicht
   * versehentlich fuer SKU-Werte haelt. Diese Ansicht ZEIGT die SKU, und fuer
   * den Leser ist „Schutzart IP66" dieselbe Angabe, ob sie an der SKU oder am
   * Modell steht. Also werden beide zusammengefuehrt; die Menge der geerbten
   * IDs entscheidet an genau zwei Stellen: an der leisen Marke in der Zeile
   * und beim Speichern (ein geerbter Wert wird nie geaendert, sondern an der
   * SKU ueberschrieben).
   */
  const [geerbtIds, setGeerbtIds] = useState<ReadonlySet<number>>(new Set());
  const [modell, setModell] = useState<{ readonly code: string } | null>(null);
  const [befunde, setBefunde] = useState<readonly ArticleFinding[]>([]);
  const [vorschlaege, setVorschlaege] = useState<readonly ValueProposalRef[]>([]);
  /*
   * LEUCHTEN JE LEITUNGSSCHUTZSCHALTER -- kein Merkmal, und das mit Grund.
   *
   * „12" allein ist keine Aussage. Erst Charakteristik UND Nennstrom machen
   * daraus eine: an einem B16 haengen zwoelf Leuchten, an einem C16
   * achtzehn. Migration 0016 hat dafuer eine eigene Tabelle angelegt, statt
   * einen Mehrfachwert mit drei Bedeutungen je Zeile zu erfinden.
   *
   * Die Angabe kommt aus derselben Aktenantwort wie die Vorschlaege -- sie
   * wird ohnehin geholt, und ein zweiter Abruf waere ein zweiter Weg zu
   * denselben Daten.
   */
  const [schalter, setSchalter] = useState<readonly BreakerCapacity[]>([]);
  /* Die verbauten Komponenten -- vor allem der Treiber. Sie stehen in
     `article_relations` und nicht als Merkmal: der Treiber ist ein
     eigener Artikel, kein Textfeld an der Leuchte. */
  const [komponenten, setKomponenten] = useState<readonly ArticleRelationRef[]>([]);
  /* Was an diesem Artikel nachgewiesen ist -- Eigenschaft samt Normen. */
  const [eigenschaften, setEigenschaften] = useState<readonly ArticleCapability[]>([]);
  const [error, setError] = useState<unknown>(null);

  const [entwurf, setEntwurf] = useState<Record<number, Feld>>({});
  const [feldfehler, setFeldfehler] = useState<Record<number, string>>({});
  const [speichert, setSpeichert] = useState(false);
  /** Was der letzte Speicherlauf ergeben hat -- auch der halbe Erfolg. */
  const [bericht, setBericht] = useState<{ ok: number; offen: number } | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  /* Welches Merkmal beim Oeffnen des Anlegeformulars vorgewaehlt ist --
     gesetzt von „Information ergaenzen" (Produktdaten 001 A5). */
  const [vorwahl, setVorwahl] = useState<string | null>(null);
  /*
   * WAS IN DIESER BEARBEITUNG BEREITS GESPEICHERT WURDE.
   *
   * Der Wert steht damit in der Datenbank -- und ist genau deshalb NICHT
   * mehr fachlich bestaetigt: der Server nimmt eine Bestaetigung bei jeder
   * Wertaenderung zurueck. Zwischen „gespeichert" und „bestaetigt" liegt
   * eine eigene Handlung, und dieser Merkzettel ist es, der sie an Ort und
   * Stelle anbieten laesst, statt sie in einen zweiten Bearbeitungsdurchlauf
   * zu schicken.
   *
   * Er lebt nur so lange wie der Bearbeitungsmodus. Beim Verlassen ist die
   * Aussage „gerade eben gespeichert" nicht mehr wahr; was bleibt, ist der
   * Pruefstand am Wert, und der steht ohnehin da.
   */
  const [gespeichert, setGespeichert] = useState<readonly number[]>([]);
  /*
   * DER ENTWURF EINER MEHRFACHAUSWAHL -- nach MERKMAL, nicht nach Wert.
   *
   * Ein Mehrfach-Enum wie `certification` ist in den Daten KEINE Zeile,
   * sondern eine Menge von Zeilen: jede Zertifizierung ist ein eigener
   * Merkmalswert mit eigener ID. „CE (LVD) und RoHS ankreuzen, ENEC
   * abwaehlen" ist deshalb kein Aendern eines Wertes, sondern ein Anlegen
   * und ein Loeschen.
   *
   * Der uebrige Entwurf ist nach Wert-ID geordnet -- das geht hier nicht: der
   * hinzuzufuegende Wert hat noch keine ID, und der zu entfernende soll seine
   * verlieren. Der Schluessel ist deshalb der MERKMALSCODE, und der Inhalt
   * ist die Menge der gewaehlten Auswahlcodes.
   *
   * Gespeichert wird erst beim Speichern -- wie ueberall in dieser Ansicht.
   * Ein Haken, der sofort schreibt, waere ein Schreibvorgang je Mausklick.
   */
  const [mehrfach, setMehrfach] = useState<Record<string, readonly string[]>>({});

  /* Die Achsencodes der Ausfuehrung -- eine Menge, damit die Zeile sie in
     einem Schritt beantworten kann. */
  const achsen = useMemo(
    () => new Set((ausfuehrung ?? []).map((v) => v.definition)),
    [ausfuehrung]);
  const [anforderungen, setAnforderungen] = useState<ArticleRequirements | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.articleProperties(articleId);
      const erbe = r.inherited?.items ?? [];
      setItems([...r.items, ...erbe]);
      setGeerbtIds(new Set(erbe.map((v) => v.id)));
      setModell(r.inherited?.model ?? null);
      setBefunde(r.findings ?? []);
    } catch (e) { setError(e); }
    /*
     * Die offenen Aenderungsvorschlaege kommen aus der Aktenantwort.
     *
     * Sie sind KEINE Merkmalsdaten, sondern Widersprueche zwischen einer
     * Quelle und dem Bestand -- und sie gehoeren an das Feld, dem sie
     * widersprechen. Bis hierher waren sie ausschliesslich in der Akte zu
     * sehen; wer in der Technik arbeitete, sah den alten Wert, als sei nichts.
     *
     * Scheitert diese zweite Abfrage, bleibt der Bereich benutzbar: die
     * Vorschlaege fehlen dann, die Merkmale nicht.
     */
    try {
      const d = await api.articleDossier(articleId);
      setVorschlaege(d.openProposals);
      setSchalter(d.breakerCapacities);
      setKomponenten(d.relations.filter((r) => r.relationType === "verwendet_komponente"));
    } catch { setVorschlaege([]); setSchalter([]); setKomponenten([]); }
    /*
     * Die erwarteten Informationen (Produktdaten 001 A5).
     *
     * Eine EIGENE, kleine Abfrage -- und sie darf scheitern, ohne den Bereich
     * mitzunehmen: ohne sie fehlen die Luecken, die Merkmale nicht.
     */
    try {
      setAnforderungen((await api.articleRequirements(articleId)));
    } catch { setAnforderungen(null); }
    try {
      setEigenschaften((await api.articleCapabilities(articleId)).items);
    } catch { setEigenschaften([]); }
  }, [articleId]);

  useEffect(() => {
    setItems(null); setBefunde([]); setVorschlaege([]); setSchalter([]);
    setKomponenten([]); setEigenschaften([]);
    setEntwurf({}); setFeldfehler({}); setBericht(null); setNeuOffen(false);
    setGespeichert([]); setMehrfach({});
    void load();
  }, [load]);

  /*
   * Beim Verlassen des Bearbeitungsmodus endet auch der Merkzettel.
   *
   * „Gerade eben gespeichert" ist eine Aussage ueber DIESEN Arbeitsgang. Wer
   * den Modus erneut oeffnet, faengt einen neuen an -- was vom letzten bleibt,
   * ist der Pruefstand am Wert, und der steht ohnehin da.
   */
  useEffect(() => {
    if (!editing) { setGespeichert([]); setBericht(null); }
  }, [editing]);

  /** Die Werte nach Merkmal -- die Grundlage jeder Zeile. */
  const nachDefinition = useMemo(() => {
    const m = new Map<string, PropertyValue[]>();
    for (const v of items ?? []) {
      const l = m.get(v.definition) ?? [];
      l.push(v);
      m.set(v.definition, l);
    }
    return m;
  }, [items]);

  const erlaubt = useMemo(
    () => meta.propertyDefinitions.filter((d) => d.allowsArticle),
    [meta.propertyDefinitions]);

  /* Die Gruppen, die MERKMALSWERTE tragen. Was sonst noch in eine Gruppe
     gehoert -- Treiber, Schalter, Nachweise --, kommt erst in `alleGruppen`
     dazu; hier zaehlt allein der Merkmalskatalog. */
  /*
   * WAS ERWARTET WIRD, ABER FEHLT, STEHT JETZT AUCH DA (Produktdaten 001 A5).
   *
   * Bis hierher zeigte diese Ansicht ausschliesslich, was gepflegt IST. Ein
   * Merkmal ohne Wert war unsichtbar -- man musste wissen, dass es existiert,
   * um es ueber „Merkmal hinzufuegen" zu finden. Die Folge stand in den
   * Daten: von 171 Hauptprodukten trug EINES eine Konformitaetsangabe.
   *
   * Eine fehlende erwartete Information ist KEIN Fehler und keine Sperre.
   * Sie ist Arbeitsvorrat -- und der gehoert dorthin, wo gearbeitet wird.
   */
  const erwartetOhneWert = useMemo(() => {
    const m = new Map<string, RequirementCheck>();
    for (const c of anforderungen?.items ?? []) {
      if (c.subjectKind !== "property") continue;
      if (c.state === "vorhanden_belastbar" && c.source !== "abgeleitet") continue;
      if (c.state === "vorhanden_unbestaetigt") continue;
      if (nachDefinition.has(c.subject)) continue;   // hat einen Wert: normale Zeile
      m.set(c.subject, c);
    }
    /* Auch die Mitglieder einer offenen Alternativgruppe -- aber nur, solange
       KEINE Variante erfuellt ist. Ist der Durchmesser da, fehlt die Laenge
       nicht. */
    for (const g of anforderungen?.groups ?? []) {
      if (g.fulfilledBy !== null) continue;
      for (const v of g.variants) {
        for (const c of v.members) {
          if (!nachDefinition.has(c.subject)) m.set(c.subject, c);
        }
      }
    }
    return m;
  }, [anforderungen, nachDefinition]);

  /*
   * WIE DIESE LEUCHTE GELIEFERT WIRD -- und warum das die Anzeige angeht.
   *
   * `dc_output_voltage` und `dc_output_current` heissen im Katalog nach ihrer
   * TREIBERSEITE. An einer Leuchte ohne oder mit separatem Betriebsgeraet
   * stehen dieselben Zahlen fuer das Gegenteil: den Bereich, in dem sie
   * betrieben werden darf. Welcher der beiden Faelle vorliegt, sagt allein
   * der gepflegte Lieferzustand -- nicht die Kategorie, nicht der Name.
   *
   * Steht er nicht da, wird nichts unterstellt: dann bleibt der Katalogname.
   * Die Umbenennung selbst macht `propertyLabel`; hier wird nur gesagt, woran
   * das Merkmal gerade steht.
   */
  const kontext: Merkmalskontext = useMemo(
    () => ({ controlGearSupply:
      nachDefinition.get("control_gear_supply")?.[0]?.code ?? null }),
    [nachDefinition]);

  const gruppen: readonly Gruppe[] = useMemo(() => PROPERTY_GROUPS
    .map((code) => ({
      code,
      zeilen: erlaubt
        .filter((d) => d.group === code
          && (nachDefinition.has(d.code) || erwartetOhneWert.has(d.code)))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((def) => ({
          def,
          values: nachDefinition.get(def.code) ?? [],
          befunde: befunde.filter((f) => f.definition === def.code),
          vorschlaege: vorschlaege.filter((v) => v.definition === def.code),
          geerbt: modell && (nachDefinition.get(def.code) ?? []).length > 0
            && nachDefinition.get(def.code)!.every((v) => geerbtIds.has(v.id))
            ? modell.code : null,
          ausAusfuehrung: achsen.has(def.code),
          anforderung: anforderungen?.items.find((c) => c.subject === def.code)
            ?? [...erwartetOhneWert.values()].find((c) => c.subject === def.code)
            ?? null,
        })),
    }))
    .filter((g) => g.zeilen.length > 0),
    [erlaubt, nachDefinition, befunde, vorschlaege, modell, geerbtIds, achsen,
     anforderungen, erwartetOhneWert]);

  /*
   * EINE AUSSAGE DARF NICHT AN EINER FREMDEN ANGABE HAENGEN.
   *
   * Eine Gruppenkarte entstand bis hierher nur, wenn die Gruppe MERKMALS-
   * WERTE traegt. Das genuegt nicht mehr, denn drei Dinge gehoeren fachlich
   * in eine Gruppe, ohne Merkmalswerte zu sein: der verbaute Treiber und die
   * Leuchten je Leitungsschutzschalter in die Elektrik, die nachgewiesenen
   * Eigenschaften zu den Konformitaeten.
   *
   * Drei der vier HB200-U-Ausfuehrungen fuehren keine einzige Konformitaet.
   * Ohne diese Ergaenzung verschwaende dort „Ballwurfsicher nach DIN 18032-3
   * und DIN 57710-13", obwohl beide Nachweise erfasst und bestaetigt sind --
   * sichtbar waere die Eigenschaft allein an dem einen Artikel, der zufaellig
   * sieben Konformitaeten mitbringt. Eine Aussage, die von einer fremden
   * Angabe abhaengt, ist nicht gezeigt, sondern verschollen.
   *
   * Die leere Karte traegt deshalb ihre Ueberschrift und ihre Zusatzzeilen --
   * und sie entsteht NUR, wenn es diese Zusatzzeilen wirklich gibt.
   */
  const alleGruppen: readonly Gruppe[] = useMemo(() => {
    const da = new Set(gruppen.map((g) => g.code));
    const extra: Gruppe[] = [];
    if (!da.has("elektrik")
      && (komponenten.length > 0 || schalter.length > 0
        || (bearbeitet && mayWriteBreaker))) {
      extra.push({ code: "elektrik", zeilen: [] });
    }
    if (!da.has("konformitaet") && eigenschaften.length > 0) {
      extra.push({ code: "konformitaet", zeilen: [] });
    }
    return [...gruppen, ...extra];
  }, [gruppen, komponenten, schalter, eigenschaften, bearbeitet, mayWriteBreaker]);

  /*
   * Die Verteilung folgt `SPALTENORDNUNG` und nicht der Hoehe.
   *
   * Gezaehlt wird trotzdem -- aber nur fuer die Nachzuegler: eine Gruppe, die
   * in der Ordnung nicht vorkommt, landet unter der kuerzeren Spalte, statt
   * gar nicht zu erscheinen.
   */
  const spalten = useMemo(() => {
    const s: Gruppe[][] = [[], []];
    for (const [i, codes] of SPALTENORDNUNG.entries()) {
      for (const code of codes) {
        const g = alleGruppen.find((x) => x.code === code);
        if (g) s[i]!.push(g);
      }
    }
    for (const g of alleGruppen) {
      if (SPALTENORDNUNG.some((codes) => codes.includes(g.code))) continue;
      s[s[0]!.length <= s[1]!.length ? 0 : 1]!.push(g);
    }
    return s;
  }, [alleGruppen]);

  const fehlend = useMemo(
    () => erlaubt.filter((d) => !nachDefinition.has(d.code)),
    [erlaubt, nachDefinition]);

  /*
   * DER PRUEF- UND BEFUNDSTAND -- EINMAL OBEN STATT DREIUNDZWANZIGMAL DANEBEN.
   *
   * Bis hierher trug im LESEMODUS jeder noch nicht bestaetigte Wert seinen
   * eigenen Pruefstand. Am Referenzartikel HB200-U4KD2D-85-CRI sind das
   * dreiundzwanzig gleichlautende Hinweise -- und dazwischen vier echte
   * Befunde und ein offener Vorschlag, also genau das, was man sehen soll.
   * Der Hinweis war nicht falsch; er war dreiundzwanzigmal derselbe, und
   * damit ein Muster, ueber das man hinwegliest.
   *
   * Die Aussage geht nicht verloren, sie wandert: einmal, als Zahl, nach oben.
   *
   * DAS IST KEINE VOLLSTAENDIGKEITSAMPEL. Gezaehlt wird, was DA IST -- nicht,
   * was fehlt. „22 Werte fehlen", „Technik unvollstaendig" und „4 % fertig"
   * waeren Urteile ueber den Datenbestand; „noch nicht bestaetigt" ist eine
   * Auskunft ueber unseren Pruefstand und sonst nichts.
   *
   * Gezaehlt wird ausserdem NUR, was in dieser Ansicht auch wirklich steht --
   * die Werte der gerenderten Zeilen. Eine Zahl, zu der sich die Zeile nicht
   * finden laesst, waere schlimmer als keine Zahl.
   */
  const stand: Stand = useMemo(() => {
    const zeilen = gruppen.flatMap((g) => g.zeilen);
    const werte = zeilen.flatMap((z) => z.values);
    return {
      werte: werte.length,
      /*
       * `null` heisst „der Pruefstand wurde gar nicht geliefert".
       *
       * Ohne `property.review.read` fehlt `quality` an JEDEM Wert. Dann darf
       * hier auch keine Bestaetigungszahl stehen: sie waere genau die
       * Auskunft, die das Recht zurueckhaelt -- nur eben aufaddiert.
       */
      bestaetigt: werte.some((v) => v.quality !== null)
        ? werte.filter((v) => v.quality === "belastbar").length
        : null,
      befunde: zeilen.reduce((n, z) => n + z.befunde.length, 0),
      vorschlaege: zeilen.reduce(
        (n, z) => n + z.vorschlaege.filter((v) => !v.alreadyApplied).length, 0),
    };
  }, [gruppen]);

  /** Der Ausgangswert eines Feldes -- fuer den Vergleich mit dem Entwurf. */
  const original = useCallback((id: number): Feld => {
    const v = (items ?? []).find((x) => x.id === id);
    return v ? ausWert(v) : {};
  }, [items]);

  const geaendert = useCallback((id: number): boolean => {
    const e = entwurf[id];
    return e !== undefined && !gleich(e, original(id));
  }, [entwurf, original]);

  const offeneIds = useMemo(
    () => Object.keys(entwurf).map(Number).filter((id) => geaendert(id)),
    [entwurf, geaendert]);

  /* Die gespeicherte Auswahl eines Mehrfach-Enums -- als Menge von Codes. */
  const istMenge = useCallback((defCode: string): readonly string[] =>
    (items ?? []).filter((v) => v.definition === defCode && v.code !== null)
      .map((v) => v.code!).sort(),
    [items]);

  /* Welche Mehrfachmerkmale im Entwurf tatsaechlich abweichen. */
  const offeneMengen = useMemo(() => Object.keys(mehrfach).filter((code) => {
    const soll = [...(mehrfach[code] ?? [])].sort();
    const ist = istMenge(code);
    return soll.length !== ist.length || soll.some((c, i) => c !== ist[i]);
  }), [mehrfach, istMenge]);

  const dirty = offeneIds.length > 0 || offeneMengen.length > 0;

  const setFeld = (id: number, k: string, v: string | boolean): void => {
    setBericht(null);
    setEntwurf((s) => ({ ...s, [id]: { ...(s[id] ?? original(id)), [k]: v } }));
    setFeldfehler((f) => { const n = { ...f }; delete n[id]; return n; });
  };

  const verwerfen = useCallback((): void => {
    setEntwurf({}); setFeldfehler({}); setBericht(null); setNeuOffen(false);
    setGespeichert([]); setMehrfach({});
  }, []);

  /*
   * SPEICHERN -- WERT FUER WERT, MIT AUSDRUECKLICHEM TEILERFOLG.
   *
   * Ein Merkmalswert ist ein eigener Datensatz mit eigener Adresse; fuenf
   * geaenderte Werte sind fuenf Aufrufe. Genau deshalb muss der halbe Erfolg
   * eine eigene Antwort haben: schlaegt der dritte fehl, sind die ersten
   * beiden trotzdem gespeichert. Diese Ansicht behauptet dann NICHT, alles
   * sei gesichert -- sie sagt, wie viele es waren, laesst die uebrigen
   * Eingaben stehen und benennt den Fehler an seinem Feld.
   */
  const speichern = useCallback(async (): Promise<boolean> => {
    const ids = Object.keys(entwurf).map(Number).filter((id) => {
      const e = entwurf[id]!;
      const v = (items ?? []).find((x) => x.id === id);
      return v !== undefined && !gleich(e, ausWert(v));
    });
    const mengenIds = offeneMengen;
    if (ids.length === 0 && mengenIds.length === 0) return true;

    setSpeichert(true);
    setError(null);
    const fehler: Record<number, string> = {};
    const bleiben: Record<number, Feld> = {};
    const mengenFehler: Record<string, string> = {};
    const bleibenMengen: Record<string, readonly string[]> = {};
    const frisch: number[] = [];
    /*
     * GEZAEHLT WIRD DIE AENDERUNG, NICHT DER AUFRUF.
     *
     * „Bei CE (LVD) und RoHS ankreuzen und ENEC abwaehlen" sind drei Aufrufe
     * und EINE Aenderung. „3 Änderungen gespeichert" waere richtig gerechnet
     * und falsch berichtet -- der Nutzer hat an einem Merkmal gearbeitet.
     */
    let ok = 0;

    /*
     * EIN GEERBTER WERT WIRD UEBERSCHRIEBEN, NICHT GEAENDERT.
     *
     * Er gehoert dem Produktmodell. Ihn hier zu aendern, aenderte ihn an
     * allen Ausfuehrungen -- aus der Akte EINER SKU heraus, ohne dass der
     * Bearbeiter das sieht. Stattdessen entsteht an dieser SKU ein eigener
     * Wert; ab dann erbt sie zu dieser Definition nichts mehr.
     *
     * Traegt das Modell zu der Definition mehrere Werte, werden die nicht
     * angefassten mit uebernommen. Sonst verschwaenden sie still: die Regel
     * lautet „eigener Wert ersetzt geerbten" je DEFINITION, nicht je Index.
     */
    const erbDefs = new Set<string>();
    for (const id of ids) {
      const v = (items ?? []).find((x) => x.id === id)!;
      if (geerbtIds.has(id)) { erbDefs.add(v.definition); continue; }
      const def = erlaubt.find((d) => d.code === v.definition)!;
      const pruefung = pruefe(def, entwurf[id]!, i18n);
      if (pruefung.fehler) {
        fehler[id] = pruefung.fehler;
        bleiben[id] = entwurf[id]!;
        continue;
      }
      try {
        await api.updateProperty(id, pruefung.payload);
        frisch.push(id);
        ok++;
      } catch (e) {
        fehler[id] = errorText(i18n, e);
        bleiben[id] = entwurf[id]!;
      }
    }

    for (const defCode of erbDefs) {
      const def = erlaubt.find((d) => d.code === defCode)!;
      const geerbte = (items ?? []).filter((v) => v.definition === defCode)
        .sort((a, b) => a.valueIndex - b.valueIndex);
      const geaenderte = geerbte.filter((v) => ids.includes(v.id));
      const pruefungen = geerbte.map((v) => ids.includes(v.id)
        ? pruefe(def, entwurf[v.id]!, i18n)
        : { payload: uebernahme(v) });
      const falsch = geerbte.filter((_, i) => pruefungen[i]!.fehler);
      if (falsch.length > 0) {
        for (const v of falsch) {
          fehler[v.id] = pruefungen[geerbte.indexOf(v)]!.fehler!;
        }
        for (const v of geaenderte) bleiben[v.id] = entwurf[v.id]!;
        continue;
      }
      try {
        for (const [i, v] of geerbte.entries()) {
          const r = await api.createProperty({
            definition: defCode, articleId, valueIndex: v.valueIndex,
            origin: "intern", method: "erfasst", ...pruefungen[i]!.payload,
          } as never);
          const neu = r.items.find(
            (x) => x.definition === defCode && x.valueIndex === v.valueIndex);
          if (neu && ids.includes(v.id)) frisch.push(neu.id);
        }
        ok += geaenderte.length;
      } catch (e) {
        for (const v of geaenderte) {
          fehler[v.id] = errorText(i18n, e);
          bleiben[v.id] = entwurf[v.id]!;
        }
      }
    }

    /*
     * MEHRFACHAUSWAHL: DIE DIFFERENZ WIRD GESCHRIEBEN, NICHT DIE AUSWAHL.
     *
     * Was schon dasteht, bleibt unberuehrt -- samt seiner ID, seiner Herkunft
     * und seinem Pruefstand. Angelegt wird nur das Dazugekommene, geloescht
     * nur das Abgewaehlte. Alles andere waere ein Loeschen und Neuanlegen
     * derselben Aussage, und das verlaengert das Protokoll um Vorgaenge, die
     * nie stattgefunden haben.
     *
     * ERST LOESCHEN, DANN ANLEGEN. Der neue Index zaehlt vom bisherigen
     * Hoechstwert aufwaerts; die freigewordenen Plaetze bleiben frei. Luecken
     * sind zulaessig -- eindeutig sein muss der Index, fortlaufend nicht.
     */
    for (const defCode of mengenIds) {
      const soll = new Set(mehrfach[defCode] ?? []);
      /* Eine geerbte Auswahl wird nicht verkleinert, sondern an der SKU
         vollstaendig neu gesetzt -- geloescht wuerde sonst am Modell. */
      const erbt = (items ?? []).some((v) => v.definition === defCode && geerbtIds.has(v.id));
      const vorhanden = erbt ? [] : (items ?? []).filter((v) => v.definition === defCode);
      const weg = vorhanden.filter((v) => v.code !== null && !soll.has(v.code));
      const dazu = [...soll].filter((c) => !vorhanden.some((v) => v.code === c));
      let index = vorhanden.reduce((m, v) => Math.max(m, v.valueIndex), -1) + 1;
      try {
        for (const v of weg) await api.deleteProperty(v.id);
        for (const code of dazu) {
          const r = await api.createProperty({
            definition: defCode, articleId, valueIndex: index++,
            code, origin: "intern", method: "erfasst",
          } as never);
          /* Der neue Wert traegt die Marke „gerade gespeichert" wie jeder
             andere -- er ist genauso wenig fachlich bestaetigt. */
          const neuerWert = r.items.find(
            (x) => x.definition === defCode && x.code === code);
          if (neuerWert) frisch.push(neuerWert.id);
        }
        ok++;
      } catch (e) {
        /*
         * TEILWEISE DURCH IST MOEGLICH -- und wird nicht verschwiegen.
         *
         * Bricht der dritte von vier Aufrufen ab, stehen zwei Aenderungen
         * bereits in der Datenbank. Der Entwurf bleibt deshalb stehen, der
         * Fehler steht am Merkmal, und das anschliessende Nachladen zeigt den
         * TATSAECHLICHEN Stand -- nicht den gewuenschten.
         */
        mengenFehler[defCode] = errorText(i18n, e);
        bleibenMengen[defCode] = mehrfach[defCode] ?? [];
      }
    }

    setFeldfehler(fehler);
    setEntwurf(bleiben);
    setMehrfach(bleibenMengen);
    setBericht({ ok, offen: ids.length + mengenIds.length - ok });
    /* Frueher Gespeichertes bleibt vermerkt -- zwei Speicherlaeufe
       hintereinander sind ein Arbeitsgang und nicht zwei. */
    setGespeichert((s) => [...new Set([...s, ...frisch])]);
    setSpeichert(false);
    await load();

    /*
     * DER BEARBEITUNGSMODUS BLEIBT OFFEN -- AUCH WENN ALLES DURCH IST.
     *
     * Bis hierher endete er beim vollstaendigen Erfolg. Das war technisch
     * sauber und bedienseitig ein Umweg: die fachliche Bestaetigung ist eine
     * eigene Handlung AM WERT, sie steht in dieser Ansicht am Feld, und das
     * Feld gibt es nur im Bearbeitungsmodus. Wer also einen Wert aenderte und
     * ihn danach bestaetigen wollte, musste den Modus erneut oeffnen und
     * denselben Wert ein zweites Mal aufsuchen -- fuer eine Handlung, die
     * genau jetzt ansteht und genau hier hingehoert.
     *
     * Die fachliche TRENNUNG bleibt davon unberuehrt: gespeichert ist nicht
     * bestaetigt, der Server nimmt die Bestaetigung bei der Wertaenderung
     * zurueck, und es braucht weiterhin einen ausdruecklichen zweiten Klick.
     * Was wegfaellt, ist allein der Weg dorthin.
     *
     * Damit der Modus trotzdem einen ruhigen Ausgang hat, heisst die zweite
     * Schaltflaeche jetzt „Fertig", sobald nichts mehr offen ist -- „Abbrechen"
     * waere dort die Einladung, das eben Gespeicherte zurueckzunehmen, was sie
     * gar nicht kann.
     *
     * Der Rueckgabewert sagt weiterhin, ob alles durch ist. Daran haengt der
     * Rahmen: „Speichern und fortfahren" darf nur fortfahren, wenn wirklich
     * nichts offen blieb.
     */
    return ids.length + mengenIds.length - ok === 0;
  }, [entwurf, mehrfach, offeneMengen, items, erlaubt, i18n, load, articleId, geerbtIds]);

  /* Der Rahmen fragt ueber diese Kennung nach offenen Eingaben. */
  const speichernRef = useRef(speichern);
  speichernRef.current = speichern;
  useEffect(() => {
    steuerung.current = {
      dirty,
      save: () => speichernRef.current(),
      discard: verwerfen,
    };
    return () => { steuerung.current = null; };
  }, [dirty, verwerfen, steuerung]);

  /*
   * Auch der Browser darf die Eingaben nicht wegnehmen.
   *
   * Reiter- und Artikelwechsel fragt der Rahmen ab; das Schliessen des
   * Fensters kann er nicht abfangen. Der Standarddialog des Browsers ist
   * dafuer der einzige Weg -- er traegt keinen eigenen Text, und das ist in
   * Ordnung: er erscheint nur, wenn tatsaechlich etwas offen ist.
   */
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  if (error !== null && !items) {
    return <div className="tk"><ErrorState message={errorText(i18n, error)} onRetry={load} /></div>;
  }
  if (!items) return <div className="tk"><LoadingState label={t("props.loading")} /></div>;

  /*
   * Der Schalterblock wird zum FORMULAR, sobald jemand ihn pflegen darf.
   *
   * Im Lesemodus stehen die Kapazitaeten als gewoehnliche Zeilen im Raster --
   * dort ist „Leuchten je LS B16: 4 Leuchten" eine Angabe wie jede andere.
   * Beides gleichzeitig zu zeigen hiesse, dieselben zwei Werte untereinander
   * zu wiederholen; und wer entfernen will, sucht dann, welche der beiden
   * Listen die echte ist.
   */
  const schalterPflege = bearbeitet && mayWriteBreaker;

  return (
    <section className={bearbeitet ? "tk tk--edit" : "tk"} aria-label={t("props.section")}>
      {attention}

      {/*
        * EINE ZEILE, RUHIG UND FAKTISCH -- in BEIDEN Ansichten.
        *
        * Sie beschreibt den Artikelzustand und nicht den Bedienzustand; ihn
        * beim Betreten des Bearbeitungsmodus verschwinden zu lassen, hiesse
        * dieselbe Zahl fuer eine Eigenschaft der Ansicht auszugeben.
        *
        * Sie steht UNTER der Aufmerksamkeitszone. Die nennt die konkreten
        * Befunde und springt zu ihnen -- sie ist das Dringendere und behaelt
        * deshalb den ersten Platz.
        *
        * Kein `role="status"`: das machte aus einer stehenden Zeile eine
        * Meldung, die Vorlesewerkzeuge bei jedem Nachladen erneut ansagen.
        */}
      {/*
        * DIE STANDZEILE IST EINE PRUEFAUSKUNFT.
        *
        * „36 technische Werte · 36 fachlich bestaetigt · 2 Befunde" beschreibt
        * unseren Pflegestand, nicht das Produkt. Sie stand als erste Zeile
        * ueber den Karten und war damit das Erste, was jemand las, der wissen
        * wollte, wie hell die Leuchte ist.
        *
        * Im Bearbeitungsmodus bleibt sie -- dort ist sie der Wegweiser zu dem,
        * was noch zu tun ist, und der Sprung zu den Befunden haengt an ihr.
        */}
      {bearbeitet && stand.werte > 0 && <p className="tk__stand">{standText(stand, i18n)}</p>}

      {/*
        * DER MODUS IST BESCHRIFTET, NICHT NUR ANGEDEUTET.
        *
        * Ein aktiver Stift in der Leiste ist eine Zustandsanzeige von zwoelf
        * mal zwoelf Pixeln. Wer laenger an einem Wert sitzt, muss ohne
        * Suchen sehen, dass er im Bearbeitungsmodus ist -- und wie er wieder
        * herauskommt.
        */}
      {bearbeitet && (
        <div className="tk__modus" role="status">
          <span className="tk__moduslabel">{t("tk.editing")}</span>
          <span className="tk__modushint">{t("tk.editHint")}</span>
          <span className="tk__modusactions">
            {/*
              * NUR WAS ZU TUN IST, STEHT AUCH DA.
              *
              * Bis hierher trug die Leiste immer beide Schaltflaechen -- auch
              * unmittelbar nach dem Speichern, wenn es nichts mehr zu
              * speichern und nichts mehr zu verwerfen gab. Neben den
              * Handlungen, die am Merkmal SOFORT wirken, sah das aus wie ein
              * zweiter, globaler Pflichtdurchlauf, den es nie gab.
              *
              * Jetzt: solange etwas Getipptes offen ist, stehen „Änderungen
              * speichern" und „Abbrechen" da -- die beiden Wege, die es dann
              * wirklich gibt. Sobald nichts offen ist, bleibt „Fertig".
              *
              * NICHT entfernt wird die Speicherhandlung selbst. Sie ist die
              * einzige Stelle, an der eine getippte Zahl den Rechner
              * verlaesst; ohne sie waere jeder Tastendruck ein Schreibvorgang
              * -- und genau das soll es nicht sein.
              */}
            {dirty && (
              <button className="btn btn--primary btn--small tk__save" disabled={speichert}
                onClick={() => void speichern()}>
                {speichert ? t("props.saving") : t("tk.save")}
              </button>
            )}
            {/*
              * ZWEI BESCHRIFTUNGEN FUER EINEN AUSGANG -- weil es zwei Lagen
              * sind. Steht etwas Ungespeichertes da, ist der Klick ein
              * Verwerfen und heisst so. Steht nichts offen, gibt es nichts zu
              * verwerfen; dann waere „Abbrechen" die Andeutung, das eben
              * Gespeicherte liesse sich hier zuruecknehmen. Es laesst sich
              * nicht -- der Klick schliesst nur den Modus.
              */}
            <button className="btn btn--small tk__cancel" disabled={speichert}
              onClick={() => { verwerfen(); onEditingChange(false); }}>
              {dirty ? t("common.cancel") : t("tk.done")}
            </button>
          </span>
        </div>
      )}

      {bericht !== null && (
        <p className={bericht.offen === 0 ? "tk__bericht" : "tk__bericht tk__bericht--teil"}
           role="status">
          {bericht.offen === 0
            ? t("tk.savedAll", { n: bericht.ok })
            : bericht.ok === 0
              ? t("tk.savedNone")
              : t("tk.savedSome", { ok: bericht.ok, offen: bericht.offen })}
        </p>
      )}

      {gruppen.length === 0 && <p className="tk__empty">{t("props.empty")}</p>}

      <div className="tk__grid">
        {spalten.map((sp, i) => (
          <div className="tk__col" key={i}>
            {sp.map((g) => (
              <GruppenKarte key={g.code} gruppe={g} i18n={i18n} kontext={kontext}
                bearbeitet={bearbeitet} mayWrite={mayWrite} mayConfirm={mayConfirm}
                entwurf={entwurf} feldfehler={feldfehler} speichert={speichert}
                gespeichert={gespeichert}
                mehrfach={mehrfach} setMehrfach={setMehrfach} istMenge={istMenge}
                geaendert={geaendert} setFeld={setFeld}
                /*
                 * „Information ergaenzen" schaltet in die Bearbeitung und
                 * waehlt das Merkmal vor -- derselbe Editor, den es schon
                 * gibt, nur ohne die Suche in einer Liste von dreissig.
                 */
                onErgaenzen={mayWrite ? (code) => {
                  setVorwahl(code); setNeuOffen(true);
                  if (!bearbeitet) onEditingChange(true);
                } : undefined}
                onChanged={() => void load()} onError={setError}
                zusatz={
                  /*
                   * WAS IN DIE GRUPPE GEHOERT, ABER KEIN MERKMALSWERT IST.
                   *
                   * Bis hierher trugen der Treiber, die Schalterkapazitaeten
                   * und die Nachweise je einen eigenen Block MIT EIGENER
                   * UEBERSCHRIFT -- „Verbaute Komponenten", „Leuchten je
                   * Leitungsschutzschalter", „Nachgewiesene Eigenschaften".
                   * Drei Ueberschriften fuer fuenf Angaben, und jede sah aus
                   * wie eine eigene Technikkategorie. Wer die Marke des
                   * Treibers suchte, fand einen Fliesstext mit vier
                   * Segmenten.
                   *
                   * Jetzt stehen sie als gewoehnliche Zeilen IM Raster der
                   * Gruppe, der sie fachlich angehoeren -- gleiches
                   * LABEL-WERT-Raster, gleiche Hoehe, gleiche Ruhe. Die
                   * Daten bleiben unberuehrt: `article_relations`,
                   * `circuit_breaker_capacities` und
                   * `article_capability_evidence` werden weiterhin genau so
                   * gelesen wie zuvor. Geaendert hat sich allein, wie sie
                   * gezeigt werden.
                   */
                  g.code === "elektrik" ? (
                    <>
                      {komponenten.map((r) => (
                        <TreiberZeilen key={r.id} relation={r} i18n={i18n}
                          onOpenArticle={onOpenArticle} />
                      ))}
                      {!schalterPflege && <SchalterZeilen kapazitaeten={schalter} i18n={i18n} />}
                    </>
                  ) : g.code === "konformitaet" ? (
                    <NachweisZeilen liste={eigenschaften} i18n={i18n} bearbeitet={bearbeitet}
                      mayConfirm={mayConfirm && mayWrite} onChanged={() => void load()} />
                  ) : null
                }>
                {/*
                  * DAS FORMULAR BLEIBT EIN BLOCK -- die Zeilen sind es nicht.
                  *
                  * Anlegen und Entfernen einer Kapazitaet braucht drei
                  * Eingaben nebeneinander; in eine zweispaltige Merkmals-
                  * zeile passt das nicht ohne Verrenkung. Es steht deshalb
                  * unter den Zeilen der Gruppe und nur dann, wenn jemand
                  * tatsaechlich pflegen darf und will.
                  */}
                {g.code === "elektrik" && schalterPflege && (
                  <Schutzschalter kapazitaeten={schalter} articleId={articleId} i18n={i18n}
                    bearbeitet onChanged={() => void load()} onError={setError} />
                )}
              </GruppenKarte>
            ))}
          </div>
        ))}
      </div>

      {/*
        * HIER STAND BIS ZUM 17.09.2026 EINE EIGENE KARTE JE ZUSATZANGABE.
        *
        * „Nachgewiesene Eigenschaften" und -- an einem Artikel ohne
        * elektrische Merkmale -- „Leuchten je Leitungsschutzschalter"
        * standen als eigene Bloecke hinter dem Raster. Beide sind jetzt
        * Zeilen in ihrer Gruppenkarte; dass die Gruppe fehlen koennte, faengt
        * `alleGruppen` ab, indem es die Karte notfalls ohne Merkmalswerte
        * zeichnet. Ein Block hinter dem Raster ist dafuer nicht mehr noetig
        * -- und er war es, der aus einer Angabe eine Technikkategorie machte.
        */}

      {/* Ein Merkmal ergaenzen -- nur im Bearbeitungsmodus. Im Lesemodus waere
          es ein Formular in einer Ansicht, in der nichts geaendert wird. */}

      {bearbeitet && fehlend.length > 0 && (
        <NeuesMerkmal missing={fehlend} articleId={articleId} i18n={i18n}
          kontext={kontext}
          open={neuOffen} setOpen={setNeuOffen} vorwahl={vorwahl}
          onChanged={() => { setNeuOffen(false); setVorwahl(null); void load(); }}
          onError={setError} />
      )}

      {error !== null && <p className="tk__error" role="alert">{errorText(i18n, error)}</p>}
    </section>
  );
}

/** Eine Themengruppe als Karte: Ueberschrift, Zeilen, aufklappbare Quellen. */
function GruppenKarte({ gruppe, i18n, kontext, bearbeitet, mayWrite, mayConfirm, entwurf, feldfehler,
                       speichert, gespeichert, mehrfach, setMehrfach, istMenge,
                       geaendert, setFeld, onErgaenzen, onChanged, onError, zusatz, children }: {
  gruppe: Gruppe; i18n: I18n;
  /** Woran die Merkmale dieser Karte stehen -- siehe `propertyLabel`. */
  kontext: Merkmalskontext;
  bearbeitet: boolean; mayWrite: boolean; mayConfirm: boolean;
  entwurf: Record<number, Feld>; feldfehler: Record<number, string>; speichert: boolean;
  /** Die Wert-IDs, die in DIESER Bearbeitung bereits gespeichert wurden. */
  gespeichert: readonly number[];
  mehrfach: Record<string, readonly string[]>;
  setMehrfach: React.Dispatch<React.SetStateAction<Record<string, readonly string[]>>>;
  istMenge: (defCode: string) => readonly string[];
  geaendert: (id: number) => boolean;
  setFeld: (id: number, k: string, v: string | boolean) => void;
  onErgaenzen?: ((defCode: string) => void) | undefined;
  onChanged: () => void; onError: (e: unknown) => void;
  /*
   * Zeilen, die fachlich in diese Gruppe gehoeren, aber kein Merkmalswert
   * sind -- Treiber, Schalterkapazitaeten, Nachweise.
   *
   * Sie stehen INNERHALB von `tk__rows` und damit in demselben Raster wie
   * die Merkmalszeilen. Das ist der ganze Unterschied zu `children`: was
   * hinter dem Raster steht, faengt eine eigene Spur an und springt optisch
   * aus der Karte heraus.
   */
  zusatz?: React.ReactNode;
  /** Was unter den Zeilen steht -- heute allein das Schalterformular. */
  children?: React.ReactNode;
}): React.JSX.Element {
  const t = i18n.t;
  const name = i18n.tCode("propGroup", gruppe.code, gruppe.code);
  /* Die Quellwortlaute der ganzen Gruppe an einer Stelle -- je Wert eine
     Zeile, damit keiner verschwindet. */
  const quellen = gruppe.zeilen.flatMap((z) =>
    z.values.filter((v) => v.sourceSegment).map((v) => ({
      key: v.id,
      merkmal: propertyLabel(i18n, z.def, kontext),
      wortlaut: v.sourceSegment!,
    })));

  return (
    <section className="tk__group" aria-label={name}>
      <h4 className="tk__grouptitle">{name}</h4>
      <div className="tk__rows">
        {gruppe.zeilen.map((z) => (
          <MerkmalZeile key={z.def.code} zeile={z} i18n={i18n} kontext={kontext}
            gruppenname={name}
            bearbeitet={bearbeitet} mayWrite={mayWrite} mayConfirm={mayConfirm}
            entwurf={entwurf} feldfehler={feldfehler} speichert={speichert}
            gespeichert={gespeichert}
            mehrfach={mehrfach} setMehrfach={setMehrfach} istMenge={istMenge}
            geaendert={geaendert} setFeld={setFeld} onErgaenzen={onErgaenzen}
            onChanged={onChanged} onError={onError} />
        ))}
        {zusatz}
      </div>
      {/*
        * „QUELLEN DIESER GRUPPE" GEHOERT NICHT UNTER JEDE KARTE.
        *
        * Der Quellwortlaut sagt, WOHER wir einen Wert haben -- eine Frage der
        * Herkunft, nicht des Produktes. Unter jeder Gruppenkarte stand sie
        * als achte Zeile und machte aus fuenf ruhigen Karten fuenf mit
        * Anhang. Im Bearbeitungsmodus steht sie weiterhin da, und der
        * Arbeitsbereich Herkunft fuehrt sie ohnehin.
        */}
      {bearbeitet && quellen.length > 0 && (
        <details className="tk__prov props__prov">
          <summary className="tk__provsummary">{t("tk.sources")}</summary>
          <ul className="tk__provlist">
            {quellen.map((q) => (
              <li className="tk__source props__source" key={q.key}>
                <span className="tk__sourcename">{q.merkmal}</span>
                <span className="tk__sourcetext">{q.wortlaut}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {children}
    </section>
  );
}

/**
 * Eine Zeile: Bezeichnung links, Wert rechts.
 *
 * Die Kennung `merkmal-<code>` ist ein Vertrag mit der Aufmerksamkeitszone:
 * von dort springt ein Klick hierher. Sie darf sich nicht aendern.
 *
 * `props__q` und `props__befund` stehen zusaetzlich zu den eigenen Klassen.
 * Sie sind KEIN Rest, sondern die gemeinsame Bedeutung: „Pruefstand" und
 * „Befund" sehen ueberall in HOMEBASE gleich aus, und die Pruefungen
 * erkennen sie daran.
 */
function MerkmalZeile({ zeile, i18n, kontext, gruppenname, bearbeitet, mayWrite, mayConfirm,
                        entwurf, feldfehler, speichert, gespeichert,
                        mehrfach, setMehrfach, istMenge,
                        geaendert, setFeld, onErgaenzen, onChanged, onError }: {
  zeile: Zeile; i18n: I18n;
  /** Woran dieses Merkmal steht -- siehe `propertyLabel`. */
  kontext: Merkmalskontext;
  /** Die Ueberschrift der Gruppe -- siehe `beschriftet` weiter unten. */
  gruppenname: string;
  bearbeitet: boolean; mayWrite: boolean; mayConfirm: boolean;
  entwurf: Record<number, Feld>; feldfehler: Record<number, string>; speichert: boolean;
  /** Die Wert-IDs, die in DIESER Bearbeitung bereits gespeichert wurden. */
  gespeichert: readonly number[];
  mehrfach: Record<string, readonly string[]>;
  setMehrfach: React.Dispatch<React.SetStateAction<Record<string, readonly string[]>>>;
  istMenge: (defCode: string) => readonly string[];
  geaendert: (id: number) => boolean;
  setFeld: (id: number, k: string, v: string | boolean) => void;
  /** „Information ergaenzen" -- oeffnet den vorhandenen Editor mit diesem Merkmal. */
  onErgaenzen?: ((defCode: string) => void) | undefined;
  onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const { def, values, befunde, vorschlaege } = zeile;
  const name = propertyLabel(i18n, def, kontext);

  /*
   * EIN MEHRFACH-ENUM IST KEINE ZEILE MIT EINEM WERT, SONDERN EINE AUSWAHL.
   *
   * `certification`, `ip_rating`, `light_colour`, `control` und `sensor`
   * tragen mehrere Werte nebeneinander -- jeder eine eigene Zeile in der
   * Datenbank, alle dieselbe Aussage: WELCHE davon gelten. Sie verdienen
   * deshalb im Lesemodus eine Kachel je Eintrag statt einer Aufzaehlung, und
   * im Bearbeitungsmodus eine Auswahl statt eines Feldes je vorhandenem Wert.
   */
  const mehrfachEnum = def.valueType === "enum" && def.isMulti;

  /*
   * EINE BESCHRIFTUNG, DIE NUR DIE UEBERSCHRIFT WIEDERHOLT, SAGT NICHTS.
   *
   * Traegt eine Gruppe genau ein Merkmal und heisst dieses wie die Gruppe,
   * stuenden „Zertifizierungen & Konformitäten" und darunter noch einmal
   * dasselbe. Die Regel ist bewusst allgemein und kennt keinen
   * Merkmalscode -- sie greift, wo sie zutrifft, und sonst nirgends.
   */
  const beschriftet = name !== gruppenname;

  /*
   * Der Pruefstand steht nur da, wenn er GELIEFERT wurde.
   *
   * Ohne `property.review.read` liefert der Server `quality` gar nicht erst.
   * `null` heisst deshalb nie „kein Status" -- es heisst „geht dich nichts
   * an", und dann erscheint auch keine Farbe und keine Beschriftung.
   */
  const stufen = [...new Set(values.map((v) => v.quality))]
    .filter((q): q is QualityStatusCode => q !== null);
  const unbestaetigt = stufen.some((q) => q !== "belastbar");
  /*
   * WAS EIN KONFLIKT IST -- UND WAS NUR EINE AUFGABE IM NACHBARSYSTEM.
   *
   * Hier zaehlte jeder Befund. Damit trug eine Zeile den Warnstreifen auch
   * dann, wenn fachlich laengst entschieden war, dass HOMEBASE recht hat und
   * DTE nachzuziehen ist -- „IP66 gegen DTE IP65" sah aus wie eine offene
   * Frage und war eine erledigte.
   *
   * Ein Befund mit `dteKorrektur` ist deshalb kein Konflikt dieser Zeile. Er
   * verschwindet nicht, er wird nur leise: der Wert steht ruhig da, der
   * Unterschied steht dezent daneben.
   */
  const echteBefunde = befunde.filter((f) => !f.dteKorrektur);
  const konflikt = echteBefunde.length > 0 || vorschlaege.some((v) => !v.alreadyApplied);
  /*
   * „Geaendert" heisst bei einer Auswahl: die Menge weicht ab. Der uebrige
   * Entwurf ist nach Wert-ID geordnet und kennt diese Zeile gar nicht.
   */
  const mengeOffen = (() => {
    const soll = mehrfach[def.code];
    if (soll === undefined) return false;
    const ist = istMenge(def.code);
    const s2 = [...soll].sort();
    return s2.length !== ist.length || s2.some((c, i) => c !== ist[i]);
  })();
  const offen = values.some((v) => geaendert(v.id)) || mengeOffen;
  const fehlerhaft = values.some((v) => feldfehler[v.id] !== undefined);
  /*
   * DER FUENFTE ZUSTAND: gerade gespeichert und deshalb NICHT mehr bestaetigt.
   *
   * Er entsteht nicht aus den Daten, sondern aus dem Arbeitsgang -- die
   * Datenbank kennt keinen Unterschied zwischen einem Wert von eben und einem
   * von vorletzter Woche. Genau darum steht er nur hier und nur so lange, wie
   * der Bearbeitungsmodus laeuft.
   *
   * Er verschwindet von selbst, sobald der Wert bestaetigt ist: dann traegt er
   * `belastbar`, und `unbestaetigt` ist falsch. Es braucht dafuer kein
   * Aufraeumen und keinen zweiten Zustand, der auseinanderlaufen koennte.
   */
  const frisch = bearbeitet && unbestaetigt && !offen
    && values.some((v) => gespeichert.includes(v.id));

  /*
   * WELCHER PRUEFSTAND AM WERT STEHT -- UND WELCHER NICHT MEHR.
   *
   * Nur `belastbar`. Er ist die einzige Stufe, die ein Mensch ENTSCHIEDEN
   * hat, er ist selten, und er sagt etwas, das sonst nirgends am Wert steht:
   * dieser hier ist fachlich geprueft.
   *
   * `warnung` und `pruefung_erforderlich` sind dagegen BEIDE der Normal-
   * zustand eines noch nicht bestaetigten Wertes. Welcher von beiden ein
   * Wert traegt, haengt allein am Erfassungsweg -- `initialPropertyQuality`
   * vergibt `pruefung_erforderlich` fuer Extrahiertes und Abgeleitetes und
   * `warnung` fuer alles andere. Ueber den Wert selbst sagt der Unterschied
   * nichts, und im Lesemodus entscheidet niemand darueber.
   *
   * Am Referenzartikel tragen alle dreiundzwanzig Werte dieselbe Stufe. Zu
   * lesen war dort dreiundzwanzigmal „Prüfung erforderlich" -- und darin
   * gingen die vier Befunde unter, die tatsaechlich eine Abweichung melden.
   * Die Zahl steht jetzt einmal oben (`tk__stand`).
   *
   * IM BEARBEITUNGSMODUS BLEIBT ES AM WERT. Dort arbeitet jemand an den
   * Daten, dort ist „Noch nicht bestätigt" die Handlungsanweisung und keine
   * Randnotiz -- und der genaue Pruefstand haengt weiterhin am Titel.
   */
  /*
   * IM LESEMODUS STEHT KEINE STUFE MEHR AM WERT.
   *
   * Hier blieb „belastbar" stehen -- als einzige Stufe, aber hinter fast
   * jedem Wert. Am 17.09.2026 hat Arne die Akte durchgesehen: „IP66
   * belastbar", „160 lm/W belastbar", „5,9 kg belastbar", dreissigmal
   * dasselbe Wort. Das ist keine Produktangabe, sondern unsere Notiz
   * darueber, wie sicher wir uns sind -- und wer ein Produkt lesen will,
   * liest sie dreissigmal mit.
   *
   * Sie ist nicht verschwunden: im Bearbeitungsmodus steht sie wieder da,
   * zusammen mit den unbestaetigten Stufen, und genau dort wird sie
   * gebraucht. Ein Katalogblick ist keine Debugansicht.
   */
  const sichtbareStufen = bearbeitet ? stufen : [];
  const marken = sichtbareStufen.length > 0
    || (bearbeitet && unbestaetigt) || offen || fehlerhaft;

  const klassen = ["tk__item"];
  if (frisch) klassen.push("tk__item--gespeichert");
  if (bearbeitet && unbestaetigt) klassen.push("tk__item--unbestaetigt");
  if (konflikt) klassen.push("tk__item--konflikt");
  if (offen) klassen.push("tk__item--geaendert");
  if (fehlerhaft) klassen.push("tk__item--fehler");

  return (
    <div className={klassen.join(" ")} id={`merkmal-${def.code}`}>
      <div className={beschriftet ? "tk__row" : "tk__row tk__row--breit"}>
        {beschriftet && <span className="tk__label">{name}</span>}
        <span className={!bearbeitet && zeile.geerbt !== null ? "tk__vals tk__vals--erbe" : "tk__vals"}>
          {/*
            * EINE ERWARTETE INFORMATION OHNE WERT (Produktdaten 001 A5).
            *
            * Sachlich, nicht alarmierend: „fehlt", nicht „Fehler". Der Knopf
            * fuehrt in denselben Merkmalseditor wie jede andere Pflege -- es
            * gibt keinen zweiten Schreibweg. Ist die Information angefragt
            * oder als nicht zutreffend erklaert, steht das statt der Luecke
            * da, mit Begruendung, wo eine vorliegt.
            */}
          {values.length === 0 && zeile.anforderung && (
            <span className="tk__fehlt" data-testid="tk-erwartet"
                  data-zustand={zeile.anforderung.state} data-def={def.code}>
              {zeile.anforderung.state === "nicht_anwendbar" ? (
                <>
                  <span className="tk__fehltwort">{t("req.notApplicable")}</span>
                  {zeile.anforderung.note !== null && (
                    <span className="tk__fehltgrund"> · {zeile.anforderung.note}</span>
                  )}
                </>
              ) : zeile.anforderung.state === "angefragt" ? (
                <span className="tk__fehltwort tk__fehltwort--angefragt">{t("req.requested")}</span>
              ) : zeile.anforderung.state === "vorhanden_belastbar"
                   && zeile.anforderung.source === "abgeleitet" ? (
                /* Ein gerechneter Wert darf nie wie ein gepflegter aussehen. */
                <span className="tk__fehltwort" data-testid="tk-abgeleitet">{t("req.derived")}</span>
              ) : (
                <>
                  <span className="tk__fehltwort">{t("req.missing")}</span>
                  {/*
                    * WOHER MAN SIE BEKOMMEN KOENNTE -- mehr sagt das nicht.
                    *
                    * `supplier_obtainable` steht seit dem Anforderungsprofil
                    * am Satz und war bis hierher unsichtbar. Es beantwortet
                    * genau eine Frage: „kann ein Lieferant das ueberhaupt
                    * sagen?" Es heisst NICHT, dass jemand entschieden haette,
                    * dort zu fragen -- diese Entscheidung gibt es in HOMEBASE
                    * noch nicht, und ein Hinweis, der sie vortaeuscht, waere
                    * schlimmer als keiner.
                    *
                    * Deshalb leise: eine Beischrift neben „fehlt", kein
                    * Abzeichen, keine Farbe, kein Knopf.
                    *
                    * SIE DARF DEN LIEFERANTEN NENNEN -- ALS EINZIGE.
                    *
                    * Die Produktakte ist kundensicher: wer sie zeigt, zeigt
                    * keinen Fertigungspartner, keine Artikelnummer und keine
                    * Kondition. `AT6` und `AU1` bewachen das. Das Wort
                    * „Lieferant" war dort bis hierher ausnahmslos verboten;
                    * 02 hat am 24.09.2026 entschieden, dass GENAU DIESE
                    * Beischrift es tragen darf -- weil sie keine Beschaffung
                    * meint, sondern eine Eigenschaft der Anforderung
                    * (`supplier_obtainable`).
                    *
                    * Die Ausnahme haengt deshalb an der Kennung
                    * `tk-beschaffbar`, nicht am Wort: die Waechter zaehlen,
                    * wie oft „Lieferant" AUSSERHALB dieser Stelle steht, und
                    * verlangen null. Wer den Begriff woanders in die Akte
                    * traegt, faellt weiterhin auf.
                    */}
                  {zeile.anforderung.supplierObtainable && (
                    <span className="tk__beschaffbar" data-testid="tk-beschaffbar">
                      {t("req.supplierObtainable")}
                    </span>
                  )}
                  {!bearbeitet && mayWrite && (
                    <button type="button" className="tk__ergaenzen" data-testid="tk-ergaenzen"
                      data-def={def.code} onClick={() => onErgaenzen?.(def.code)}>
                      {t("req.add")}
                    </button>
                  )}
                </>
              )}
            </span>
          )}
          {bearbeitet && mehrfachEnum
            ? <MehrfachAuswahl def={def} values={values} i18n={i18n}
                auswahl={mehrfach[def.code] ?? istMenge(def.code)}
                mayConfirm={mayConfirm} speichert={speichert} offen={offen}
                setAuswahl={(codes) =>
                  setMehrfach((m) => ({ ...m, [def.code]: codes }))}
                onChanged={onChanged} onError={onError} />
            : bearbeitet
            ? values.map((v) => (
                <WertFeld key={v.id} def={def} value={v} i18n={i18n} kontext={kontext}
                  entwurf={entwurf[v.id]} fehler={feldfehler[v.id]}
                  mayConfirm={mayConfirm && zeile.geerbt === null} speichert={speichert}
                  geerbt={zeile.geerbt !== null}
                  geaendert={geaendert(v.id)}
                  frisch={gespeichert.includes(v.id) && !geaendert(v.id)}
                  setFeld={setFeld}
                  onChanged={onChanged} onError={onError} />
              ))
            : zeile.geerbt !== null
            /* Geerbte Werte stehen als EINE Gruppe vor der Herkunft -- sonst
               rutschte „Modell" bei mehreren Kacheln zwischen die Werte. */
            ? <span className="tk__werte">
                {values.map((v) => (
                  <span className={mehrfachEnum ? "tk__value tk__value--kachel" : "tk__value"}
                        key={v.id}>{displayValue(def, v, i18n)}</span>
                ))}
              </span>
            : values.map((v) => (
                <span className={mehrfachEnum ? "tk__value tk__value--kachel" : "tk__value"}
                      key={v.id}>{displayValue(def, v, i18n)}</span>
              ))}

          {/*
            * DIE HERKUNFT EINES GEERBTEN WERTES -- EIN WORT, LEISE.
            *
            * Kein Etikett an jedem SKU-Wert („eigener Wert"): der eigene Wert
            * ist der Normalfall und braucht keine Marke. Gekennzeichnet wird
            * nur, was nicht hier steht; der Modellcode steht im Titel, nicht
            * in der Zeile.
            */}
          {/*
            * DREI HERKUENFTE, EINE STELLE. Die Reihenfolge ist die der
            * Naehe: was die SKU selbst traegt, steht zuerst -- und ein
            * geerbter Wert ist nie zugleich ein Ausfuehrungswert.
            */}
          {zeile.geerbt !== null ? (
            <span className="tk__erbe" data-testid="tk-herkunft" data-herkunft="modell"
              title={t("tk.inheritedTitle", { model: zeile.geerbt })}>
              {t("tk.inherited")}
            </span>
          ) : zeile.ausAusfuehrung ? (
            <span className="tk__erbe tk__erbe--ausfuehrung" data-testid="tk-herkunft" data-herkunft="ausfuehrung"
              title={t("tk.fromExecutionTitle")}>
              {t("tk.fromExecution")}
            </span>
          ) : (
            <span className="tk__erbe tk__erbe--eigen" data-testid="tk-herkunft" data-herkunft="eigen"
              title={t("tk.ownValueTitle")}>
              {t("tk.ownValue")}
            </span>
          )}

          {/*
            * DER PRUEFSTAND STEHT IN DERSELBEN ZEILE WIE DER WERT.
            *
            * Als eigene Zeile darunter verdoppelte er die Hoehe der ganzen
            * Ansicht -- bei zweiundzwanzig Merkmalen sind das zweiundzwanzig
            * zusaetzliche Zeilen fuer eine Arbeitsnotiz. Reicht der Platz
            * nicht, bricht er von selbst um; erzwungen wird nichts.
            *
            * Er ist unsere Arbeitsnotiz zu einem Wert und keine Produktangabe
            * -- deshalb leise, und im Lesemodus nur dort, wo er etwas sagt
            * (siehe `sichtbareStufen`). Im Bearbeitungsmodus tritt das Wort
            * hinzu, das der Auftrag verlangt.
            */}
          {marken && (
            <span className="tk__marken">
              {sichtbareStufen.map((q) => (
                <span key={q} className={`tk__q props__q props__q--${q}`}>
                  {i18n.tCode("quality", q, q)}
                </span>
              ))}
              {/*
                * EINE MARKE, NICHT ZWEI. „Gespeichert · Noch nicht fachlich
                * bestätigt" sagt beides in einem Satz und an einer Stelle. Die
                * blosse Marke „Noch nicht bestätigt" daneben waere dieselbe
                * Auskunft ein zweites Mal -- und die eine Zeile, auf die es
                * gerade ankommt, saehe aus wie die zweiundzwanzig anderen.
                */}
              {bearbeitet && unbestaetigt && (
                <span className={frisch
                        ? "tk__mark tk__mark--gespeichert"
                        : "tk__mark tk__mark--unbestaetigt"}
                      title={stufen.filter((q) => q !== "belastbar")
                        .map((q) => i18n.tCode("quality", q, q)).join(" · ")}>
                  {frisch ? t("tk.savedUnconfirmed") : t("tk.unconfirmed")}
                </span>
              )}
              {offen && <span className="tk__mark tk__mark--geaendert">{t("tk.changed")}</span>}
              {fehlerhaft && <span className="tk__mark tk__mark--fehler">{t("tk.invalid")}</span>}
            </span>
          )}
        </span>
      </div>

      {/* Ein Befund ist NICHT der Pruefstand. Er sagt, was die Auswertung
          gesehen hat -- und er steht schon im Lesemodus da, sonst waere ein
          Konflikt erst nach dem Aufklappen erkennbar. */}
      {/*
        * DER DTE-HINWEIS IST IM LESEMODUS FORT.
        *
        * „HOMEBASE IP66 gegen DTE IP65" beschreibt keine Eigenschaft des
        * Produktes, sondern den Rueckstand eines Nachbarsystems. HOMEBASE
        * fuehrt die fachlich bestaetigte Wahrheit -- also zeigt die Technik
        * IP66, und fertig. Dass DTE nachzuziehen ist, steht als
        * Pruefhinweis am Artikel, in der Bereinigung und im
        * Bearbeitungsmodus; verloren geht nichts.
        *
        * Ein Befund OHNE diese Kennzeichnung bleibt stehen: er meldet einen
        * ungeklaerten Widerspruch am Wert selbst, und das ist sehr wohl
        * etwas, das ein Leser wissen muss.
        */}
      {befunde.filter((f) => bearbeitet || !f.dteKorrektur).map((f) => (
        <div key={f.findingCode}
          className={f.dteKorrektur
            ? "tk__befund tk__befund--dte"
            : `tk__befund props__befund props__befund--${f.findingClass}`}
          data-testid="tk-befund" data-klasse={f.findingClass}>
          <span className="tk__befundart">
            {f.dteKorrektur ? t("props.findingDte")
              : f.findingClass === "E" ? t("props.findingModel") : t("props.findingConflict")}
          </span>
          {f.detail && <span className="tk__befundtext">{f.detail}</span>}
          {/*
            * DEN GESPEICHERTEN WERT BESTAETIGEN (Produktmodelle 003 V2).
            *
            * Nur beim QUELLKONFLIKT, nicht bei der Modellabweichung: die
            * erste ist eine Frage („welche Zahl stimmt?"), die zweite ein
            * Strukturbefund, den man nicht wegklickt, sondern durch die
            * richtige Modell-/Ausfuehrungszuordnung aufloest.
            *
            * Die Begruendung ist Pflicht -- sie ist der ganze Gehalt der
            * Entscheidung. Und die Klaerung gilt fuer DIESEN Quellstand:
            * sagt die Quelle spaeter etwas anderes, steht der Widerspruch
            * wieder da.
            */}
          {mayConfirm && !f.dteKorrektur && f.findingClass !== "E" && zeile.values.length > 0 && (
            <Befundklaerung propertyId={zeile.values[0]!.id} findingCode={f.findingCode}
              i18n={i18n} onDone={onChanged} />
          )}
        </div>
      ))}

      {/*
        * EIN GEGENSTANDSLOSER VORSCHLAG IST KEINE OFFENE ARBEIT.
        *
        * Der vorgeschlagene Wert GILT bereits -- am Referenzartikel steht
        * IP66 als belastbarer Wert, und der Vorschlag lautet ebenfalls IP66.
        * Zu entscheiden ist daran nichts: `decideProposal` weist beide
        * Richtungen mit 409 `already_applied` ab, und zwar zu Recht.
        * Annehmen schriebe den Wert, der schon dasteht; Ablehnen behauptete,
        * jemand habe sich GEGEN einen gerade geltenden Wert entschieden.
        *
        * Er blieb trotzdem als Kasten stehen -- dauerhaft, unentscheidbar,
        * neben einem bestaetigten Wert. Ein Vorschlag, der nie verschwindet,
        * ist genau die Sorte Hinweis, ueber die man nach dem dritten Mal
        * hinwegliest; und danebenstehende echte Widersprueche verlieren
        * dadurch ihre Wirkung.
        *
        * ER WIRD NICHT GELOESCHT UND NICHT VERSTECKT. Die Zeile bleibt
        * unveraendert in der Datenbank, mit Wortlaut, Quelle und Zeitpunkt;
        * die Akte fuehrt sie weiter (`ProductDossier`), und die Zaehlung
        * unterscheidet sie schon heute (`proposalTally.moot` gegen `open`).
        * Was hier entfaellt, ist allein die Darstellung als ANSTEHENDE
        * ARBEIT in einem Arbeitsbereich, in dem nichts daran zu arbeiten ist.
        *
        * Loest sich der Zustand auf -- jemand aendert den Wert --, ist der
        * Vorschlag wieder ein Widerspruch und steht sofort wieder da.
        */}
      {vorschlaege.filter((v) => !v.alreadyApplied).map((v) => (
        <Vorschlag key={v.id} def={def} values={values} v={v} i18n={i18n}
          mayWrite={mayWrite} onChanged={onChanged} onError={onError} />
      ))}

      {/* Seit A5 kann eine Zeile WERTLOS sein -- eine erwartete Information, die
          fehlt. Alles, was den ersten Wert braucht, fragt vorher, ob es einen
          gibt. */}
      {values.length === 1 && feldfehler[values[0]!.id] !== undefined && (
        <p className="tk__feldfehler" role="alert">{feldfehler[values[0]!.id]}</p>
      )}
    </div>
  );
}

/**
 * Ein offener Aenderungsvorschlag -- sichtbar, aber nicht uebernommen.
 *
 * Uebernommen wird nichts von selbst. Der Bestand bleibt unveraendert, bis
 * ein Mensch entscheidet; beide Wege stehen nebeneinander und tragen ihre
 * Quelle, denn ohne sie ist die Abweichung nicht zu beurteilen.
 */
function Vorschlag({ def, values, v, i18n, mayWrite, onChanged, onError }: {
  def: PropertyDefinition; values: readonly PropertyValue[]; v: ValueProposalRef; i18n: I18n;
  mayWrite: boolean; onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [laeuft, setLaeuft] = useState(false);
  const entscheide = async (wahl: "angenommen" | "abgelehnt"): Promise<void> => {
    setLaeuft(true);
    try { await api.decideProposal(v.id, wahl); onChanged(); }
    catch (e) { onError(e); }
    finally { setLaeuft(false); }
  };
  const neu = displayValue(def, {
    id: -v.id, definition: def.code, valueIndex: 0,
    num: v.valueNum, operator: v.valueOperator, min: v.valueMin, max: v.valueMax,
    bool: null, code: v.valueCode, text: v.valueText,
    origin: "import", method: "uebernommen", quality: "pruefung_erforderlich",
    sourceSegment: null, updatedAt: "",
  }, i18n);

  return (
    <div className={v.alreadyApplied ? "tk__vorschlag tk__vorschlag--erledigt" : "tk__vorschlag"}>
      <span className="tk__vorschlaglabel">{t("props.proposalNew")}</span>
      <span className="tk__vorschlagwert">{neu}</span>
      <span className="tk__vorschlagquelle">{v.sourceNote ?? v.source}</span>
      {v.alreadyApplied ? (
        <span className="tk__vorschlagdone">{t("props.proposalAlready")}</span>
      ) : mayWrite ? (
        <span className="tk__vorschlagactions">
          <button className="btn btn--small btn--primary" disabled={laeuft}
            onClick={() => void entscheide("angenommen")}>{t("props.proposalAccept")}</button>
          <button className="btn btn--small" disabled={laeuft}
            onClick={() => void entscheide("abgelehnt")}>{t("props.proposalReject")}</button>
        </span>
      ) : (
        <span className="tk__vorschlaghint">
          {values.length > 0 && displayValue(def, values[0]!, i18n) === neu
            ? "" : t("props.proposalPending")}
        </span>
      )}
    </div>
  );
}

/**
 * Die Auswahl eines Mehrfach-Enums -- eine Zeile je moeglichem Eintrag.
 *
 * WARUM KEINE MEHRFACHLISTE UND KEINE ZWEITE OBERFLAECHE
 *
 * Ein `<select multiple>` verlangt Strg-Klick, sagt auf einem Telefon gar
 * nichts und zeigt Abgewaehltes wie Nichtvorhandenes. Eine eigene Maske
 * „Zertifizierungen verwalten" waere ein zweiter Bearbeitungsweg neben dem
 * Stift -- genau das, was diese Ansicht nicht haben soll.
 *
 * Stattdessen: der vollstaendige Wertevorrat als Kaestchen. Angehakt ist,
 * was gilt. Ergaenzen ist ein Haken, Entfernen ist ein Haken weniger. Der
 * Wertevorrat kommt aus `property_enum_values` -- diese Datei kennt keinen
 * einzigen Zertifizierungscode.
 *
 * BESTAETIGEN BLEIBT AM EINZELNEN EINTRAG.
 *
 * Jeder Haken ist in den Daten ein eigener Merkmalswert mit eigener Herkunft
 * und eigenem Pruefstand -- „ENEC ist geprueft" und „RoHS ist geprueft" sind
 * zwei Aussagen. Die Bestaetigung steht deshalb neben dem Eintrag, dem sie
 * gilt, und nicht ueber der ganzen Zeile. Und sie ist gesperrt, solange an
 * dieser Auswahl etwas Ungespeichertes steht: sonst bestaetigte jemand einen
 * Eintrag, den er im selben Atemzug abwaehlt.
 */
function MehrfachAuswahl({ def, values, i18n, auswahl, mayConfirm, speichert, offen,
                           setAuswahl, onChanged, onError }: {
  def: PropertyDefinition; values: readonly PropertyValue[]; i18n: I18n;
  auswahl: readonly string[];
  mayConfirm: boolean; speichert: boolean;
  /** Ob an dieser Zeile etwas Ungespeichertes steht. */
  offen: boolean;
  setAuswahl: (codes: readonly string[]) => void;
  onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const umschalten = (code: string, an: boolean): void => {
    setAuswahl(an ? [...auswahl, code] : auswahl.filter((c) => c !== code));
  };

  const stufe = async (id: number, code: string, q: QualityStatusCode): Promise<void> => {
    setLaeuft(code);
    try { await api.updateProperty(id, { quality: q }); onChanged(); }
    catch (e) { onError(e); }
    finally { setLaeuft(null); }
  };

  return (
    <span className="tk__auswahl">
      {def.enumValues.map((o) => {
        const gewaehlt = auswahl.includes(o.code);
        /* Der gespeicherte Wert hinter diesem Eintrag -- falls es ihn gibt. */
        const wert = values.find((v) => v.code === o.code);
        const busy = laeuft === o.code || speichert;
        return (
          <span className="tk__auswahlzeile" key={o.code}>
            <label className="tk__check">
              <input type="checkbox" checked={gewaehlt} disabled={speichert}
                onChange={(e) => umschalten(o.code, e.target.checked)} />
              <span>{i18n.tCode("propValue", o.code, o.code)}</span>
            </label>
            {/* Der Pruefstand gehoert dem gespeicherten Eintrag, nicht dem Haken. */}
            {wert !== undefined && wert.quality === "belastbar" && (
              <span className="tk__q props__q props__q--belastbar">
                {i18n.tCode("quality", "belastbar", "belastbar")}
              </span>
            )}
            {/*
              * DERSELBE NAME WIE AM EINZELWERT -- tk__aktion--pruefstand.
              *
              * Es ist dieselbe Handlung: eine Stufe am gespeicherten Wert.
              * Dass sie hier ein zweites Mal geschrieben steht, ist der
              * Mehrfachauswahl geschuldet und keine zweite Fachlichkeit.
              * Unter zwei Namen war sie fuer eine Wache nicht dieselbe
              * Handlung -- die Gegenprobe zu §20 meldete drei Zeilen ohne
              * Weg, obwohl der Weg dastand.
              */}
            {mayConfirm && wert !== undefined && wert.quality !== null
              && wert.quality !== "belastbar" && (
              <button className="tk__aktion tk__aktion--pruefstand tk__aktion--bestaetigen"
                disabled={busy || offen}
                title={offen ? t("tk.confirmBusy") : t("props.confirm")}
                onClick={() => void stufe(wert.id, o.code, "belastbar")}>
                {t("props.confirm")}
              </button>
            )}
            {mayConfirm && wert !== undefined && wert.quality === "belastbar" && (
              <button className="tk__aktion tk__aktion--pruefstand"
                disabled={busy || offen}
                title={offen ? t("tk.confirmBusy") : t("props.unconfirm")}
                onClick={() => void stufe(wert.id, o.code, "warnung")}>
                {t("props.unconfirm")}
              </button>
            )}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Leuchten je Leitungsschutzschalter.
 *
 * DREI ANGABEN, DIE NUR ZUSAMMEN ETWAS BEDEUTEN.
 *
 * Charakteristik, Nennstrom und Anzahl. „18" allein ist keine Aussage, „C16"
 * allein auch nicht. Genau deshalb ist das hier kein Merkmalswert, sondern
 * eine eigene Zeile in `circuit_breaker_capacities` -- ein Mehrfachwert
 * haette drei Bedeutungen in ein Feld gepresst.
 *
 * ANLEGEN UND ENTFERNEN WIRKEN SOFORT -- wie „Entfernen" bei einem
 * Merkmalswert und anders als eine getippte Zahl. Der Grund ist derselbe:
 * eine getippte Zahl ist ein Entwurf, eine Zeile anzulegen ist eine
 * Entscheidung. Das Formular sammelt drei Angaben und schickt sie erst,
 * wenn alle drei da sind.
 *
 * DIE SCHRANKEN KOMMEN VOM SERVER. Charakteristik A-D, Nennstrom 1-125,
 * Anzahl groesser null, und dieselbe Kombination nicht zweimal -- das steht
 * als CHECK und UNIQUE in Migration 0016. Diese Ansicht wiederholt sie
 * nicht, sie zeigt nur, was der Server antwortet.
 */
function Schutzschalter({ kapazitaeten, articleId, i18n, bearbeitet, onChanged, onError }: {
  kapazitaeten: readonly BreakerCapacity[];
  articleId: number;
  i18n: I18n;
  bearbeitet: boolean;
  onChanged: () => void;
  onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [art, setArt] = useState("B");
  const [strom, setStrom] = useState("");
  const [anzahl, setAnzahl] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const sortiert = [...kapazitaeten].sort((a, b) =>
    a.characteristic.localeCompare(b.characteristic) || a.ratedCurrent - b.ratedCurrent);

  const anlegen = async (): Promise<void> => {
    const s = Number(strom.trim()), n = Number(anzahl.trim());
    if (!Number.isInteger(s) || s < 1) { setFehler(t("tk.breakerErrCurrent")); return; }
    if (!Number.isInteger(n) || n < 1) { setFehler(t("tk.breakerErrCount")); return; }
    setBusy(true); setFehler(null);
    try {
      await api.createBreakerCapacity(articleId, {
        characteristic: art, ratedCurrent: s, maxFixtures: n,
      });
      setStrom(""); setAnzahl("");
      onChanged();
    } catch (e) { setFehler(errorText(i18n, e)); } finally { setBusy(false); }
  };

  const entfernen = async (id: number): Promise<void> => {
    setBusy(true);
    try { await api.deleteBreakerCapacity(id); onChanged(); }
    catch (e) { onError(e); } finally { setBusy(false); }
  };

  return (
    <section className="tk__schalter" aria-label={t("tk.breaker")}>
      <h4 className="tk__grouptitle">{t("tk.breaker")}</h4>
      {sortiert.length === 0 && !bearbeitet && (
        <p className="tk__empty">{t("tk.breakerEmpty")}</p>
      )}
      {sortiert.length > 0 && (
        <ul className="tk__schalterliste">
          {sortiert.map((k) => (
            <li key={k.id}>
              {/* Die Bezeichnung ist die des Schalters -- B16, C16 -- und
                  keine erfundene Kurzform. */}
              <span className="tk__schalterart">{k.characteristic}{k.ratedCurrent}</span>
              <span className="tk__schalterwert">
                {t("tk.breakerCount", { n: k.maxFixtures })}
              </span>
              {bearbeitet && (
                <button className="tk__aktion tk__aktion--weg" disabled={busy}
                  onClick={() => void entfernen(k.id)}>{t("props.remove")}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {bearbeitet && (
        <div className="tk__schalterneu">
          <label className="tk__neufeld">
            <span>{t("tk.breakerCharacteristic")}</span>
            <select className="tk__input" value={art} disabled={busy}
              onChange={(e) => setArt(e.target.value)}>
              {["A", "B", "C", "D"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
          <label className="tk__neufeld">
            <span>{t("tk.breakerCurrent")}</span>
            <span className="tk__range">
              <input className="tk__input tk__input--num" inputMode="numeric" value={strom}
                disabled={busy} onChange={(e) => setStrom(e.target.value)} />
              <span className="tk__unit">{t("unit.ampere")}</span>
            </span>
          </label>
          <label className="tk__neufeld">
            <span>{t("tk.breakerMax")}</span>
            <input className="tk__input tk__input--num" inputMode="numeric" value={anzahl}
              disabled={busy} onChange={(e) => setAnzahl(e.target.value)} />
          </label>
          <button className="btn btn--small btn--primary tk__schalteradd"
            disabled={busy || strom.trim() === "" || anzahl.trim() === ""}
            onClick={() => void anlegen()}>{t("props.add")}</button>
          {fehler !== null && <span className="tk__feldfehler" role="alert">{fehler}</span>}
        </div>
      )}
    </section>
  );
}

/* ── Eingabefelder ──────────────────────────────────────────────────────── */

/** Ein einzelner Wert im Bearbeitungsmodus. */
function WertFeld({ def, value, i18n, kontext, entwurf, fehler, mayConfirm, speichert, geaendert,
                    frisch, geerbt, setFeld, onChanged, onError }: {
  def: PropertyDefinition; value: PropertyValue; i18n: I18n; kontext: Merkmalskontext;
  entwurf: Feld | undefined; fehler: string | undefined;
  mayConfirm: boolean; speichert: boolean; geaendert: boolean;
  /** In DIESER Bearbeitung gespeichert und seither nicht wieder angefasst. */
  frisch: boolean;
  /**
   * Der Wert gehoert dem Produktmodell. Aendern darf man ihn hier -- das
   * wird beim Speichern ein eigener SKU-Wert --, entfernen nicht: es
   * entfernte ihn an allen Ausfuehrungen.
   */
  geerbt: boolean;
  setFeld: (id: number, k: string, v: string | boolean) => void;
  onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const f = entwurf ?? ausWert(value);
  const s = (k: string): string => String(f[k] ?? "");
  const id = value.id;
  const [laeuft, setLaeuft] = useState(false);
  /* Dieselbe Bezeichnung wie im Lesemodus -- eine Vorlesebeschriftung, die
     etwas anderes sagt als das Auge sieht, ist schlimmer als keine. */
  const name = propertyLabel(i18n, def, kontext);

  const stufe = async (q: QualityStatusCode): Promise<void> => {
    setLaeuft(true);
    try { await api.updateProperty(id, { quality: q }); onChanged(); }
    catch (e) { onError(e); }
    finally { setLaeuft(false); }
  };
  const entfernen = async (): Promise<void> => {
    if (!window.confirm(t("props.removeConfirm"))) return;
    setLaeuft(true);
    try { await api.deleteProperty(id); onChanged(); }
    catch (e) { onError(e); }
    finally { setLaeuft(false); }
  };

  return (
    <span className="tk__feld">
      {def.valueType === "boolean" ? (
        <label className="tk__check">
          <input type="checkbox" checked={f["bool"] === true}
            aria-label={name}
            onChange={(e) => setFeld(id, "bool", e.target.checked)} />
          <span>{t(f["bool"] === true ? "props.yes" : "props.no")}</span>
        </label>
      ) : def.valueType === "enum" ? (
        <select className="tk__input" value={s("code")} aria-label={name}
          onChange={(e) => setFeld(id, "code", e.target.value)}>
          <option value="">{t("props.chooseValue")}</option>
          {def.enumValues.map((o) => (
            <option key={o.code} value={o.code}>{i18n.tCode("propValue", o.code, o.code)}</option>
          ))}
        </select>
      ) : def.valueType === "range" ? (
        <span className="tk__range">
          <input className="tk__input tk__input--num" inputMode="decimal" value={s("min")}
            aria-label={`${name} — ${t("props.valueFrom")}`}
            onChange={(e) => setFeld(id, "min", e.target.value)} />
          <span className="tk__rangesep" aria-hidden="true">–</span>
          <input className="tk__input tk__input--num" inputMode="decimal" value={s("max")}
            aria-label={`${name} — ${t("props.valueTo")}`}
            onChange={(e) => setFeld(id, "max", e.target.value)} />
          {def.unit && <span className="tk__unit">{def.unit}</span>}
        </span>
      ) : def.valueType === "text" ? (
        <input className="tk__input" value={s("text")} aria-label={name}
          onChange={(e) => setFeld(id, "text", e.target.value)} />
      ) : (
        <span className="tk__range">
          <input className="tk__input tk__input--num" inputMode="decimal" value={s("num")}
            aria-label={name}
            onChange={(e) => setFeld(id, "num", e.target.value)} />
          {def.unit && <span className="tk__unit">{def.unit}</span>}
        </span>
      )}

      <span className="tk__wertaktionen">
        {/*
          * BESTAETIGEN IST EINE EIGENE HANDLUNG -- und sie ist gesperrt,
          * solange an diesem Wert etwas Ungespeichertes steht. Sonst
          * bestaetigte jemand den ALTEN Wert und speicherte danach den neuen,
          * der die Bestaetigung sofort wieder zuruecknimmt: zwei Klicks, ein
          * Protokolleintrag, kein Ergebnis.
          */}
        {mayConfirm && value.quality !== null && value.quality !== "belastbar" && (
          /*
            * SIE TRITT HERVOR, SOBALD SIE ANSTEHT -- und sie ist dieselbe
            * Schaltflaeche wie zuvor. Kein zweiter Weg, keine zweite Adresse,
            * dieselbe Rechtepruefung: nur die Betonung folgt dem Arbeitsgang.
            * Ein Wert, der gerade gespeichert wurde, wartet auf genau diese
            * Entscheidung; die uebrigen zweiundzwanzig warten nicht, und ihre
            * Schaltflaechen bleiben deshalb leise.
            *
            * Ausdruecklich NICHT hervorgehoben wird sie durch einen
            * Vorabfokus oder eine Vorauswahl. Ein versehentlich bestaetigter
            * Wert waere teurer als ein Klick mehr.
            */
          <button className={frisch
              ? "tk__aktion tk__aktion--pruefstand tk__aktion--bestaetigen tk__aktion--jetzt"
              : "tk__aktion tk__aktion--pruefstand tk__aktion--bestaetigen"}
            disabled={laeuft || speichert || geaendert}
            title={geaendert ? t("tk.confirmBusy") : t("props.confirm")}
            onClick={() => void stufe("belastbar")}>{t("props.confirm")}</button>
        )}
        {/*
          * AUCH DAS ZURUECKNEHMEN IST GESPERRT, SOLANGE ETWAS OFFEN IST.
          *
          * Beide Richtungen beziehen sich auf den GESPEICHERTEN Wert. Steht im
          * Feld schon etwas anderes, waere jede Entscheidung eine ueber einen
          * Stand, den niemand mehr sieht.
          */}
        {mayConfirm && value.quality === "belastbar" && (
          <button className="tk__aktion tk__aktion--pruefstand"
            disabled={laeuft || speichert || geaendert}
            title={geaendert ? t("tk.confirmBusy") : t("props.unconfirm")}
            onClick={() => void stufe("warnung")}>{t("props.unconfirm")}</button>
        )}
        {/*
          * ── KEINE SACKGASSE AM GEERBTEN WERT (§20) ─────────────────
          *
          * DER BEFUND DER ABNAHME, und die Ursache lag eine Ebene
          * hoeher als es aussah.
          *
          * An HB200-U4KD2D-85 steht „Abdeckungsmaterial:
          * Polycarbonat" und darueber „Prüfung erforderlich". Der
          * Wert gehoert aber nicht der SKU, sondern dem PRODUKTMODELL
          * HB200-U -- die SKU erbt ihn. Deshalb steht
          * `mayConfirm && zeile.geerbt === null` am Aufruf dieser
          * Komponente, und deshalb gab es hier keinen Knopf.
          *
          * DAS IST FACHLICH RICHTIG: wer den Wert an der SKU
          * bestaetigte, entschiede ueber einen Wert, der fuer VIER
          * Ausfuehrungen gilt. Die Bestaetigung gehoert an das
          * Modell.
          *
          * FALSCH WAR, DASS ES NICHT DASTAND. Der Pruefstand war zu
          * sehen, die Handlung fehlte, und nichts sagte warum. Jetzt
          * sagt die Zeile, WO entschieden wird -- das ist der
          * Unterschied zwischen „nicht hier" und „nirgends".
          */}
        {!mayConfirm && geerbt && value.quality !== null
          && value.quality !== "belastbar" && (
          <span className="tk__geerbthinweis">{t("tk.confirmAtModel")}</span>
        )}
        {!geerbt && (
          <button className="tk__aktion tk__aktion--weg" disabled={laeuft || speichert}
            onClick={() => void entfernen()}>{t("props.remove")}</button>
        )}
      </span>

      {fehler !== undefined && <span className="tk__feldfehler" role="alert">{fehler}</span>}
    </span>
  );
}

/**
 * Ein fehlendes Merkmal ergaenzen.
 *
 * Bewusst dieselbe Adresse wie ueberall (`POST /properties`) und dieselbe
 * Rechtepruefung. Es entsteht kein zweiter Schreibweg.
 */
function NeuesMerkmal({ missing, articleId, i18n, kontext, open, setOpen, vorwahl,
                        onChanged, onError }: {
  missing: readonly PropertyDefinition[]; articleId: number; i18n: I18n;
  /** Woran die angebotenen Merkmale stehen -- siehe `propertyLabel`. */
  kontext: Merkmalskontext;
  open: boolean; setOpen: (v: boolean) => void;
  /** Vorgewaehltes Merkmal aus „Information ergaenzen" -- null: das erste. */
  vorwahl?: string | null;
  onChanged: () => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [code, setCode] = useState(missing[0]!.code);
  /* Kommt der Anwender ueber eine bestimmte Luecke, steht sie schon da --
     derselbe Editor, nur ohne die Suche in einer Liste von dreissig. */
  useEffect(() => {
    if (vorwahl && missing.some((d) => d.code === vorwahl)) setCode(vorwahl);
  }, [vorwahl, missing]);

  /*
   * UND ER SIEHT ES AUCH (Restpunkte 001, korrigiert in 003).
   *
   * „Information ergaenzen" oeffnet den Bearbeitungsmodus und waehlt das
   * richtige Merkmal -- aber das Formular steht am ENDE der Technik, im
   * Bearbeitungsmodus mehrere Bildschirmhoehen tiefer. Ohne den Sprung
   * klickt der Anwender auf eine Luecke und sieht danach den Seitenkopf.
   *
   * DREI FASSUNGEN, UND WARUM ES JETZT DIE DRITTE IST
   *
   *   1. Sprung, Fokus 350 ms spaeter. Ein Rennen: raeumt ein Neuaufbau in
   *      dieser Spanne den Timer weg, bleibt der Fokus aus. An P60-4K
   *      gemessen eingetreten.
   *   2. Fokus sofort, danach WEICHER Lauf. Der Fokus sass, der Lauf lief --
   *      in Chrome. Produktiv kam der Anwender trotzdem nicht am Formular
   *      an. Nachgemessen in Chrome bei 1440x900, 1280x720, 1440x1100 und
   *      1100x640: jedes Mal richtig. Der Fehler liess sich hier also nicht
   *      nachstellen -- und eine Fassung, deren Richtigkeit ich nur an
   *      EINEM Browser zeigen kann, ist keine.
   *   3. Diese. Sie verlaesst sich auf nichts, was verhandelbar ist.
   *
   * WAS AN FASSUNG 2 VERHANDELBAR WAR
   *
   *   * Der weiche Lauf dauert eine knappe halbe Sekunde. In dieser Zeit
   *     bricht ihn JEDE Scrollbewegung des Anwenders ab -- und er nimmt ihn
   *     nicht wieder auf. Wer nach dem Klick nichts sieht und selbst zu
   *     scrollen beginnt, verhindert damit genau den Sprung, auf den er
   *     wartet.
   *   * Der Effekt lief ZWEIMAL: einmal beim Einhaengen, als `code` noch das
   *     erste Merkmal der Liste war, und einmal, wenn die Vorwahl greift.
   *     Zwei Laeufe uebereinander, der zweite waehrend des ersten.
   *   * `block: "center"` ist fuer das LETZTE Element unerreichbar. Gemessen
   *     endet der Behaelter jedes Mal an seinem Anschlag; was dabei
   *     herauskommt, haengt daran, wie ein Browser diesen Wunsch beschneidet.
   *
   * DESHALB JETZT: EIN Lauf, erst wenn die Vorwahl steht, nach einem
   * Bildaufbau, OHNE Animation -- und danach wird NACHGESEHEN. Bringt
   * `scrollIntoView` das Formular nicht vollstaendig in den sichtbaren
   * Bereich seines Behaelters, wird dieser selbst gesetzt. Ein gesetzter
   * `scrollTop` laesst sich nicht abbrechen und nicht beschneiden.
   */
  const rahmen = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    /*
     * Erst handeln, wenn die Vorwahl WIRKLICH steht. Der Effekt darueber
     * setzt `code` einen Durchlauf spaeter; bis dahin zeigt das Formular
     * noch ein anderes Merkmal, und ein Sprung dorthin waere ein Sprung zur
     * falschen Antwort. Bietet die Liste die Vorwahl gar nicht an, gibt es
     * nichts zu warten.
     */
    const steht = code === vorwahl || !missing.some((d) => d.code === vorwahl);
    if (!open || !vorwahl || !steht) return;
    const el = rahmen.current;
    if (!el) return;
    /* Ein Bildaufbau spaeter -- dann hat der Browser das eben eingehaengte
       Formular wirklich vermessen. */
    /* Der Weg dorthin steht einmal im Haus -- siehe `lib/fokus.ts`. */
    return zeigeUndFokussiere(el, el.querySelector<HTMLElement>(
      ".tk__neufeld:nth-of-type(2) input, .tk__neufeld:nth-of-type(2) select,"
      + " .tk__neufeld:nth-of-type(2) textarea"));
  }, [open, vorwahl, code, missing]);
  const [f, setF] = useState<Feld>({});
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const def = missing.find((d) => d.code === code) ?? missing[0]!;
  const s = (k: string): string => String(f[k] ?? "");

  if (!open) {
    return <button className="btn tk__add" onClick={() => setOpen(true)}>{t("props.add")}</button>;
  }

  const anlegen = async (): Promise<void> => {
    const p = pruefe(def, f, i18n);
    if (p.fehler) { setFehler(p.fehler); return; }
    setBusy(true); setFehler(null);
    try {
      await api.createProperty({
        definition: def.code, articleId, origin: "intern", method: "erfasst",
        ...p.payload,
      } as never);
      setF({});
      onChanged();
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  return (
    <div className="tk__neu" id="tk-neues-merkmal" ref={rahmen}>
      <label className="tk__neufeld">
        <span>{t("props.chooseProperty")}</span>
        <select value={code} onChange={(e) => { setCode(e.target.value); setF({}); setFehler(null); }}>
          {missing.map((d) => (
            <option key={d.code} value={d.code}>{propertyLabel(i18n, d, kontext)}</option>
          ))}
        </select>
      </label>
      <label className="tk__neufeld">
        <span>{t("props.value")}{def.unit ? ` (${def.unit})` : ""}</span>
        {def.valueType === "boolean" ? (
          <input type="checkbox" checked={f["bool"] === true}
            onChange={(e) => setF((x) => ({ ...x, bool: e.target.checked }))} />
        ) : def.valueType === "enum" ? (
          <select value={s("code")} onChange={(e) => setF((x) => ({ ...x, code: e.target.value }))}>
            <option value="">{t("props.chooseValue")}</option>
            {def.enumValues.map((o) => (
              <option key={o.code} value={o.code}>{i18n.tCode("propValue", o.code, o.code)}</option>
            ))}
          </select>
        ) : def.valueType === "range" ? (
          <span className="tk__range">
            <input className="tk__input tk__input--num" inputMode="decimal" value={s("min")}
              onChange={(e) => setF((x) => ({ ...x, min: e.target.value }))} />
            <span className="tk__rangesep" aria-hidden="true">–</span>
            <input className="tk__input tk__input--num" inputMode="decimal" value={s("max")}
              onChange={(e) => setF((x) => ({ ...x, max: e.target.value }))} />
          </span>
        ) : def.valueType === "text" ? (
          <input value={s("text")} onChange={(e) => setF((x) => ({ ...x, text: e.target.value }))} />
        ) : (
          <input inputMode="decimal" value={s("num")}
            onChange={(e) => setF((x) => ({ ...x, num: e.target.value }))} />
        )}
      </label>
      <span className="tk__neuactions">
        <button className="btn btn--primary btn--small" disabled={busy} onClick={() => void anlegen()}>
          {busy ? t("props.saving") : t("props.save")}
        </button>
        <button className="btn btn--small" onClick={() => { setOpen(false); setFehler(null); }}>
          {t("common.cancel")}
        </button>
      </span>
      {fehler !== null && <p className="tk__feldfehler" role="alert">{fehler}</p>}
    </div>
  );
}

/* ── Pruefung der Eingaben ───────────────────────────────────────────────── */

/**
 * Ist diese Eingabe speicherbar -- und was wird gesandt?
 *
 * DREI GRUNDSAETZE
 *
 *   1. Der Fehler steht AM FELD. „Bitte pruefen Sie Ihre Eingaben" nennt
 *      weder die Stelle noch den Grund.
 *   2. Es wird NICHTS still zurechtgebogen. Aus „12,5" wird „12.5" -- das ist
 *      dieselbe Zahl in anderer Schreibweise und keine fachliche Korrektur.
 *      Aus „ungefaehr 12" wird kein Wert, sondern ein Fehler.
 *   3. Die Grenzen des Katalogs (`min_num`, `max_num`) prueft der SERVER. Sie
 *      hier zu wiederholen hiesse, dieselbe Regel an zwei Stellen zu pflegen
 *      -- und die zweite laeuft irgendwann der ersten hinterher.
 */
export function pruefe(def: PropertyDefinition, f: Feld, i18n: I18n):
  { fehler?: string; payload: Record<string, unknown> } {
  const t = i18n.t;
  const zahl = (roh: string): number | null => {
    const x = roh.trim().replace(",", ".");
    if (x === "") return null;
    return /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : Number.NaN;
  };
  const alsText = (roh: string): string => roh.trim().replace(",", ".");

  switch (def.valueType) {
    case "boolean":
      return { payload: { bool: f["bool"] === true } };
    case "enum": {
      const code = String(f["code"] ?? "").trim();
      if (code === "") return { fehler: t("tk.errEmpty"), payload: {} };
      return { payload: { code } };
    }
    case "text": {
      const text = String(f["text"] ?? "").trim();
      if (text === "") return { fehler: t("tk.errEmpty"), payload: {} };
      return { payload: { text } };
    }
    case "range": {
      const min = zahl(String(f["min"] ?? ""));
      const max = zahl(String(f["max"] ?? ""));
      if (Number.isNaN(min) || Number.isNaN(max)) return { fehler: t("tk.errNumber"), payload: {} };
      if (min === null && max === null) return { fehler: t("tk.errEmpty"), payload: {} };
      if (min !== null && max !== null && min > max) return { fehler: t("tk.errOrder"), payload: {} };
      return { payload: {
        min: min === null ? null : alsText(String(f["min"])),
        max: max === null ? null : alsText(String(f["max"])),
      } };
    }
    default: {
      const n = zahl(String(f["num"] ?? ""));
      if (n === null) return { fehler: t("tk.errEmpty"), payload: {} };
      if (Number.isNaN(n)) return { fehler: t("tk.errNumber"), payload: {} };
      if (def.valueType === "integer" && !Number.isInteger(n)) {
        return { fehler: t("tk.errInteger"), payload: {} };
      }
      return { payload: { num: alsText(String(f["num"])) } };
    }
  }
}

/**
 * Ein geerbter Wert, wie er an der SKU neu angelegt wird, wenn ein ANDERER
 * Wert derselben Definition ueberschrieben wird -- unveraendert uebernommen.
 */
export function uebernahme(v: PropertyValue): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (v.num !== null) p["num"] = v.num;
  if (v.min !== null) p["min"] = v.min;
  if (v.max !== null) p["max"] = v.max;
  if (v.bool !== null) p["bool"] = v.bool;
  if (v.code !== null) p["code"] = v.code;
  if (v.text !== null) p["text"] = v.text;
  return p;
}

/* ── Zeilen, die kein Merkmalswert sind ─────────────────────────────────── */

/**
 * Eine Zusatzzeile im Raster der Merkmale.
 *
 * Sie traegt bewusst dieselben Klassen wie eine Merkmalszeile (`tk__item`,
 * `tk__row`, `tk__label`, `tk__vals`). Eine eigene Gestaltung waere hier eine
 * Auskunft, die es nicht gibt: fuer den Leser ist „Treiber Marke: SOSEN"
 * dasselbe wie „Schutzart: IP66" -- eine Angabe zu diesem Artikel. Dass die
 * eine aus `article_relations` und die andere aus `property_values` kommt,
 * ist unsere Buchhaltung und nicht seine Frage.
 *
 * KEINE KENNUNG `merkmal-<code>`. Die gehoert den Merkmalszeilen und ist der
 * Vertrag mit der Aufmerksamkeitszone; eine Zeile ohne Merkmal haette dort
 * kein Ziel, zu dem gesprungen werden koennte.
 */
/**
 * Einen Quellkonflikt klaeren -- mit Begruendung (Produktmodelle 003 V2).
 *
 * Bis hierher gab es den Endpunkt und keine Bedienung: der gelbe Kasten
 * blieb stehen, bis jemand den WERT aenderte -- also genau das tat, was
 * fachlich falsch gewesen waere, wenn der gespeicherte Wert stimmt.
 */
function Befundklaerung({ propertyId, findingCode, i18n, onDone }: {
  propertyId: number; findingCode: string; i18n: I18n; onDone: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  if (!offen) {
    return (
      <button type="button" className="btn btn--small tk__klaeren" data-testid="tk-klaeren"
        onClick={() => setOffen(true)}>{t("tk.confirmStored")}</button>
    );
  }
  return (
    <div className="tk__klaerform" data-testid="tk-klaerform">
      <label className="feld">
        <span className="feld__label">{t("tk.clarifyReason")}</span>
        <input className="feld__input" data-testid="tk-klaergrund" value={grund} autoFocus
          maxLength={1000} onChange={(e) => setGrund(e.target.value)} />
        <small className="feld__note">{t("tk.clarifyNote")}</small>
      </label>
      <div className="models__formactions">
        <button className="btn btn--primary btn--small" data-testid="tk-klaerspeichern"
          disabled={busy || grund.trim() === ""}
          onClick={() => {
            setBusy(true); setFehler(null);
            void api.clarifyFinding(propertyId, findingCode, grund.trim())
              .then(() => { setOffen(false); setGrund(""); onDone(); })
              .catch((e) => setFehler(e))
              .finally(() => setBusy(false));
          }}>{t("tk.clarifySave")}</button>
        <button className="btn btn--small" disabled={busy}
          onClick={() => { setOffen(false); setFehler(null); }}>{t("common.cancel")}</button>
      </div>
      {fehler !== null && <p className="tk__fehler" role="alert">{errorText(i18n, fehler)}</p>}
    </div>
  );
}

function ZusatzZeile({ label, children }: {
  label: string; children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="tk__item">
      <div className="tk__row">
        <span className="tk__label">{label}</span>
        <span className="tk__vals">{children}</span>
      </div>
    </div>
  );
}

/**
 * Der verbaute Treiber als zwei Zeilen: Marke und Modell.
 *
 * MARKE UND MODELL STEHEN NICHT AN DER LEUCHTE -- und das bleibt so. Der
 * Treiber ist ein eigener Artikel mit eigenem Lieferanten und eigenem
 * Ersatzteilstatus; ein Merkmal `driver_brand` an der Leuchte waere eine
 * zweite Treiberwahrheit, die beim naechsten Treiberwechsel stehen bliebe.
 * Migration 0016 hat sie deshalb ausdruecklich abgelehnt.
 *
 * BIS 0098 STAND HIER EINE TEXTZERLEGUNG -- und sie war falsch.
 *
 * Weil die Marke nirgends einen Ort hatte, schnitt diese Funktion sie aus
 * der Kurzbeschreibung des Zielartikels: `split("|")`, Segment 1 die
 * Marke, Segment 2 das Modell. Das setzte ein Format voraus, das nie
 * vereinbart war. Nachgerechnet an allen 27 Treibern der Familie
 * `Treiber`: 15 haetten ein FALSCHES Modell gezeigt -- „24 W" fuer
 * LF-GIF030YA0600, „200 CA-SERIE" fuer SS-200CA-56DH, „≤ 24 W" fuer
 * BK-BTL028-600mA. Sichtbar war es nicht, weil nur drei SOSEN-Treiber
 * zugeordnet sind und deren Kurzbeschreibung zufaellig passte.
 *
 * JETZT KOMMEN BEIDE AUS EINER STRUKTUR.
 *
 *   Marke   `manufacturer_brand` des Zielartikels (0098), mitgeliefert
 *           als `relation.target.brand`
 *   Modell  `relation.target.articleNumber` -- die Artikelnummer IST das
 *           Modell, sie war es immer
 *
 * Das ist weiterhin keine zweite Wahrheit: gelesen wird jedes Mal neu,
 * was am Komponentenartikel steht. Aendert sich dort die Marke, aendert
 * sie sich hier -- nur eben als Angabe und nicht als Zufall einer
 * Zeichenkette.
 *
 * FEHLT DIE MARKE, FEHLT DIE ZEILE. Kein Rueckfall auf die
 * Kurzbeschreibung, kein Ableiten aus der Artikelnummer, kein „—". Eine
 * leere Zeile „Treiber Marke: —" behauptete, es gebe keine Marke;
 * tatsaechlich ist sie nur nicht erfasst. Dasselbe Muster wie bei der
 * Bestueckung in der Uebersicht: kein Wert, kein Eintrag.
 *
 * Der VERWEIS bleibt dabei immer da -- das Modell ist die Artikelnummer
 * und die gibt es an jedem Artikel.
 */
function TreiberZeilen({ relation, i18n, onOpenArticle }: {
  relation: ArticleRelationRef;
  i18n: I18n;
  onOpenArticle: (id: number) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const marke = relation.target.brand;
  const modell = relation.target.articleNumber;

  return (
    <>
      {marke !== null && (
        <ZusatzZeile label={t("tk.driverBrand")}>
          <span className="tk__value">{i18n.tCode("propValue", marke, marke)}</span>
        </ZusatzZeile>
      )}
      {/*
        * Der Klick fuehrt zum Treiberartikel -- wie bisher, nur ohne eigenen
        * Block darum herum. Wer ihn pflegen will, pflegt ihn an einer Stelle,
        * und die ist von hier aus einen Klick entfernt.
        */}
      <ZusatzZeile label={t("tk.driverModel")}>
        <button type="button" className="tk__komponentenlink"
          onClick={() => onOpenArticle(relation.target.id)}
          title={t("tk.componentOpen", { number: relation.target.articleNumber })}>
          {modell}
        </button>
      </ZusatzZeile>
    </>
  );
}

/**
 * Die Leuchten je Leitungsschutzschalter als Zeilen -- eine je Schalter.
 *
 * DIE BESCHRIFTUNG IST DER SCHALTER, DER WERT IST DIE ANZAHL. „Leuchten je
 * LS B16" links, „4 Leuchten" rechts. Damit steht die Angabe in demselben
 * Raster wie „Bemessungsspannung" darueber, und die beiden Schalter lassen
 * sich untereinander vergleichen, ohne zu lesen.
 *
 * OHNE DIE EINHEIT „A" HINTER DER BEZEICHNUNG. „B16" IST der Name des
 * Schalters, so wie er auf dem Schalter steht -- „B16 A" liest sich wie eine
 * Groesse mit Einheit und ist keine.
 */
function SchalterZeilen({ kapazitaeten, i18n }: {
  kapazitaeten: readonly BreakerCapacity[]; i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const sortiert = [...kapazitaeten].sort((a, b) =>
    a.characteristic.localeCompare(b.characteristic) || a.ratedCurrent - b.ratedCurrent);
  return (
    <>
      {sortiert.map((k) => (
        <ZusatzZeile key={k.id}
          label={t("tk.breakerRow", { breaker: `${k.characteristic}${k.ratedCurrent}` })}>
          <span className="tk__value">{t("tk.breakerCount", { n: k.maxFixtures })}</span>
        </ZusatzZeile>
      ))}
    </>
  );
}

/**
 * NACHGEWIESENE EIGENSCHAFTEN IN DERSELBEN LISTE WIE DIE ZERTIFIKATE
 * (Browser-Befunde 001, Teil A).
 *
 * Fuer den Leser ist „Ballwurfsicher" dasselbe wie „ENEC": eine Zusage, die
 * erst mit Beleg belastbar ist. Die eigene Zeile „Nachgewiesene
 * Eigenschaften" machte daraus eine zweite Art von Aussage. Jetzt steht die
 * Eigenschaft in der Liste „Zertifizierungen, Konformitäten & Nachweise" --
 * in der Ansicht als Kachel, im Bearbeiten als Zeile wie CE oder RoHS.
 *
 * INTERN BLEIBT ES GETRENNT. Zertifizierungen und Konformitaeten sind Werte
 * des Merkmals `certification`; eine nachgewiesene Eigenschaft ist das
 * Modell aus 0050 (`article_capability_evidence`, je Norm ein Nachweis).
 *
 * KEIN FREIES ANHAKEN. Die Eigenschaft besteht, weil ein Nachweis mit Norm an
 * ihr haengt -- der Haken ist deshalb gesetzt und nicht umschaltbar; Nachweise
 * werden unter Dokumente gepflegt. Bestaetigen und Zuruecknehmen setzen den
 * Pruefstand ALLER Nachweise dieser Eigenschaft ueber den bestehenden
 * Endpunkt (`article.write`, fuer „belastbar" zusaetzlich `property.confirm`).
 * Scheitert einer davon, steht es da -- mit Zahl und Grund.
 *
 * DIE NORM STEHT KLEIN DAHINTER, die vollen Titel im Titelhinweis. Die
 * Textbildung nimmt weiterhin nur bestaetigte Nachweise.
 */
function NachweisZeilen({ liste, i18n, bearbeitet, mayConfirm, onChanged }: {
  liste: readonly ArticleCapability[]; i18n: I18n;
  bearbeitet: boolean; mayConfirm: boolean; onChanged: () => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  if (liste.length === 0) return null;

  const stufe = async (c: ArticleCapability, ziel: QualityStatusCode): Promise<void> => {
    setLaeuft(c.capability); setMeldung(null);
    const offen = c.evidence.filter((e) => e.quality !== ziel);
    let ok = 0; let fehler: unknown = null;
    for (const e of offen) {
      try { await api.setEvidenceQuality(e.id, ziel); ok++; }
      catch (err) { fehler = err; }
    }
    if (fehler !== null) {
      setMeldung(t("tk.evidencePartial", { ok, total: offen.length, error: errorText(i18n, fehler) }));
    }
    setLaeuft(null);
    onChanged();
  };

  return (
    <div className="tk__item tk__item--nachweis">
      <div className="tk__row tk__row--breit">
        <span className="tk__vals">
          {bearbeitet ? (
            <span className="tk__auswahl">
              {liste.map((c) => {
                const normen = c.evidence.map((e) => e.standard.code).join(" · ");
                const titel = c.evidence.map((e) => `${e.standard.code}: ${e.standard.title}`).join("\n");
                const belastbar = c.evidence.length > 0 && c.evidence.every((e) => e.quality === "belastbar");
                const busy = laeuft === c.capability;
                return (
                  <span className="tk__auswahlzeile" key={c.capability}>
                    <label className="tk__check" title={t("tk.evidenceFromDocuments")}>
                      <input type="checkbox" checked disabled readOnly />
                      <span>{i18n.tCode("capability", c.capability, c.capability)}</span>
                    </label>
                    {normen !== "" && <span className="tk__normenkurz" title={titel}>{normen}</span>}
                    {belastbar ? (
                      <span className="tk__q props__q props__q--belastbar">
                        {i18n.tCode("quality", "belastbar", "belastbar")}
                      </span>
                    ) : (
                      <span className="tk__normoffen" title={t("tk.evidenceOpenHint")}>{t("tk.evidenceOpen")}</span>
                    )}
                    {mayConfirm && !belastbar && (
                      <button className="tk__aktion" disabled={busy}
                        onClick={() => void stufe(c, "belastbar")}>{t("props.confirm")}</button>
                    )}
                    {mayConfirm && belastbar && (
                      <button className="tk__aktion" disabled={busy}
                        onClick={() => void stufe(c, "warnung")}>{t("props.unconfirm")}</button>
                    )}
                  </span>
                );
              })}
            </span>
          ) : liste.map((c) => {
            const normen = c.evidence.map((e) => e.standard.code).join(" · ");
            const titel = c.evidence.map((e) => `${e.standard.code}: ${e.standard.title}`).join("\n");
            const offen = c.evidence.some((e) => e.quality !== "belastbar");
            return (
              <span className="tk__nachweis" key={c.capability}>
                <span className="tk__value tk__value--kachel" title={titel}>
                  {i18n.tCode("capability", c.capability, c.capability)}
                </span>
                {offen && (
                  <span className="tk__normoffen" title={t("tk.evidenceOpenHint")}>{t("tk.evidenceOpen")}</span>
                )}
                {normen !== "" && <span className="tk__normenkurz" title={titel}>{normen}</span>}
              </span>
            );
          })}
          {meldung !== null && <span className="tk__fehler" role="alert">{meldung}</span>}
        </span>
      </div>
    </div>
  );
}
